import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { getProjects, createProjects, updateProjects } from '../api/projectsAPI';
import {
  getPlantRatesByProjectId,
  getPlantRateCategoriesByPlantRateId,
  createPlantRates,
  updatePlantRates,
  deletePlantRates,
  calculatePlantRateDepreciation,
  calculatePlantRateInterest,
  calculatePlantRateInsurance,
  calculatePlantRatePlanningOccurrences,
  calculatePlantRateTable
} from '../api/plant-ratesAPI';
import { getCurrency } from '../api/currencyAPI';
import { checkGroupNameExists, createGroup, getGroups } from '../api/groupAPI';
import { getMeasurementUnits } from '../api/measurement-unitsAPI';
import { getClass } from '../api/classAPI';
import { getManufacturer } from '../api/manufacturerAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useTableFilters } from '../utils/useTableFilters';
import { useValidator, validateForm } from '../utils/useValidator';
import { getPurchasePriceItems, updatePurchasePriceItems } from '../api/purchase-price-itemsAPI';
import { getCustomers } from '../api/customersAPI';
import { getProjectStatus } from '../api/project-statusAPI';
import { getPlantRateTyres, createPlantRateTyres, updatePlantRateTyres } from '../api/plant-rate-tyresAPI';
import { updatePlantRateCategories } from '../api/plant-rate-categoriesAPI';
import { createPlantRateFuelTemplates, getFuelTemplatesByGroup, getPlantRateFuelTemplates, updatePlantRateFuelTemplates } from '../api/plant-rate-fuel-templatesAPI';
import { createPlantRateFluids, deletePlantRateFluids, getPlantRateFluids, getPlantRateFluidsByPlantRateId, replacePlantRateFluidsFromTemplate, updatePlantRateFluids } from '../api/plant-rate-fluidsAPI';
import { getPlantRateFluidList } from '../api/plant-rate-fluid-listAPI';
import { createPlantRateGetList, deletePlantRateGetList, getPlantRateGetByPlantRateId, getPlantRateGetListTemplates, replacePlantRateGetFromTemplate, updatePlantRateGetList } from '../api/plant-rate-get-listAPI';
import { getExpenseCodes } from '../api/expense-codeAPI';
import { getTyreTypes } from '../api/tyre-typesAPI';
import { getPlantRateGlobals } from '../api/plant-rate-globalsAPI';
import { HierarchySelect } from './common/HierarchySelect';
import Select from 'react-select';
import PlantHireRateForm from './PlantHireRateForm';
import { createTasks, deleteTasks, getTasksByPlantRate } from '../api/tasksAPI';
import TaskEditModal from './child/TaskEditModal';

const breakdownProfiles = [
  { value: 'flat', label: 'Flat' },
  { value: 'increasing_linear', label: 'Increasing Linear' },
  { value: 'increasing_exponential', label: 'Increasing Exponential' },
];
const insuranceCalculationMethodOptions = [
  { value: 'percent_purchase_price', label: 'Percentage of Purchase Price' },
  { value: 'percent_average_value', label: 'Percentage Average Value' },
  { value: 'fixed', label: 'Fixed Monthly Amount' },
];
const marginMethodOptions = [
  { value: 'percent', label: 'Percentage' },
  { value: 'fixed usage', label: 'Fixed by Usage' },
  { value: 'fixed month', label: 'Fixed By Month' },
];
const depreciationBasisOptions = [
  { value: 'none', label: 'None' },
  { value: 'straight_line', label: 'Straight Line' },
  { value: 'declining_balance_time', label: 'Declining Balance (Time)' },
  { value: 'declining_balance_hours', label: 'Declining Balance (Hours)' },
];
const calculateDefaultDepreciationMonths = (startUsage, endUsage) => {
  const rawMonths = (Number(endUsage || 0) - Number(startUsage || 0)) / 8760 / 12;
  return Number.isFinite(rawMonths) ? Math.trunc(rawMonths) : 0;
};
const formatWholeNumber = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return '0';
  return Math.round(numericValue).toLocaleString('en-US');
};

const PlantRatesLayer = () => {
  const userProfile = useUserProfile();
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [plantRates, setPlantRates] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [measurementUnits, setMeasurementUnits] = useState([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectForm, setProjectForm] = useState({});
  const [projectErrors, setProjectErrors] = useState({});
  const [showPlantRateModal, setShowPlantRateModal] = useState(false);
  const [showEditPlantRateModal, setShowEditPlantRateModal] = useState(false);
  const [editingPlantRate, setEditingPlantRate] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [endMode, setEndMode] = useState('usage');
  const [plantRateForm, setPlantRateForm] = useState({ startUsage: 0, utilisation: 1000, endUsage: 10000 });
  const [openProjectGroupKeys, setOpenProjectGroupKeys] = useState({});
  const [openPlantRateInputGroupKeys, setOpenPlantRateInputGroupKeys] = useState({});
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [newGroup, setNewGroup] = useState({});
  const [classes, setClasses] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showPurchasePriceModal, setShowPurchasePriceModal] = useState(false);
  const [purchasePriceItems, setPurchasePriceItems] = useState([]);
  const [purchasePriceItemsRateId, setPurchasePriceItemsRateId] = useState(null);
  const [purchasePriceItemErrors, setPurchasePriceItemErrors] = useState({});
  const [showTyreSetupModal, setShowTyreSetupModal] = useState(false);
  const [showDepreciationModal, setShowDepreciationModal] = useState(false);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [showOverheadSetupModal, setShowOverheadSetupModal] = useState(false);
  const [showMarginSetupModal, setShowMarginSetupModal] = useState(false);
  const [showMinimumMonthlyChargeModal, setShowMinimumMonthlyChargeModal] = useState(false);
  const [tyreSetupForm, setTyreSetupForm] = useState({ includeRims: 0 });
  const [editingPlantRateTyre, setEditingPlantRateTyre] = useState(null);
  const [tyreTypes, setTyreTypes] = useState([]);
  const [plantRateCategories, setPlantRateCategories] = useState([]);
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [showPlantRateCategoriesModal, setShowPlantRateCategoriesModal] = useState(false);
  const [editInputDraft, setEditInputDraft] = useState({});
  const [dirtyPlantRateFields, setDirtyPlantRateFields] = useState({});
  const [showFuelUsageModal, setShowFuelUsageModal] = useState(false);
  const [showMaintenanceTasksModal, setShowMaintenanceTasksModal] = useState(false);
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [maintenanceTaskSearch, setMaintenanceTaskSearch] = useState('');
  const [maintenanceTaskErrors, setMaintenanceTaskErrors] = useState({});
  const [editingMaintenanceTaskId, setEditingMaintenanceTaskId] = useState(null);
  const [newMaintenanceTaskId, setNewMaintenanceTaskId] = useState(null);
  const [fuelUsageForm, setFuelUsageForm] = useState({});
  const [editingFuelTemplate, setEditingFuelTemplate] = useState(null);
  const [fuelTemplateProfiles, setFuelTemplateProfiles] = useState([]);
  const [plantRateGlobals, setPlantRateGlobals] = useState([]);
  const [showOilFluidsModal, setShowOilFluidsModal] = useState(false);
  const [plantRateFluids, setPlantRateFluids] = useState([]);
  const [fluidTemplatesForGroup, setFluidTemplatesForGroup] = useState([]);
  const [fluidItems, setFluidItems] = useState([]);
  const [showFluidEditModal, setShowFluidEditModal] = useState(false);
  const [plantRateTableValues, setPlantRateTableValues] = useState({});
  const [planningOccurrencesResult, setPlanningOccurrencesResult] = useState(null);
  const [fluidForm, setFluidForm] = useState({});
  const [editingFluid, setEditingFluid] = useState(null);
  const [overheadSetupForm, setOverheadSetupForm] = useState({ overheadPercent: 0 });
  const [marginSetupForm, setMarginSetupForm] = useState({ marginMethod: 'percent', marginPercent: 0, marginFixed: 0 });
  const [minimumMonthlyChargeForm, setMinimumMonthlyChargeForm] = useState({ minimumBasis: 'hours', minimumHours: 0, minimumCharge: 0 });
  const [showGetSetupModal, setShowGetSetupModal] = useState(false);
  const [plantRateGets, setPlantRateGets] = useState([]);
  const [showGetEditModal, setShowGetEditModal] = useState(false);
  const [getForm, setGetForm] = useState({});
  const [editingGet, setEditingGet] = useState(null);
  const [hasGetTemplatesForGroup, setHasGetTemplatesForGroup] = useState(false);

  const minimumBasisOptions = useMemo(() => ([
    { value: 'hours', label: 'Minimum Monthly Hours' },
    { value: 'fixed', label: 'Fixed Monthly Charge' },
    { value: 'ownership', label: 'Monthly Ownership Costs at Minimum Hours' },
    { value: 'day_rate', label: 'Minimum Day Rate' },
  ]), []);
  const minimumMonthlyChargeFields = useMemo(() => ([
    { field: 'minimumBasis', label: 'Minimum Basis', type: 'select', size: '12', required: true, options: minimumBasisOptions },
    { field: 'minimumHours', label: 'Minimum Monthly Hours', type: 'number', size: '12', min: 0, max: 744, condition: (form) => ['hours', 'ownership'].includes(form.minimumBasis) },
    { field: 'minimumCharge', label: 'Minimum Monthly Charge', type: 'number', size: '12', mustBePositiveDecimal: true, condition: (form) => ['fixed', 'ownership', 'day_rate'].includes(form.minimumBasis) },
  ]), [minimumBasisOptions]);
  const { errors: minimumMonthlyChargeErrors, setErrors: setMinimumMonthlyChargeErrors } = useValidator(minimumMonthlyChargeFields, minimumMonthlyChargeForm);
  const { renderField: renderMinimumMonthlyChargeField } = useFormBuilder(minimumMonthlyChargeFields, minimumMonthlyChargeForm, setMinimumMonthlyChargeForm, minimumMonthlyChargeErrors);


  const canEdit = userProfile?.privileges?.includes('plant-rates:update');
  const canDelete = userProfile?.privileges?.includes('plant-rates:delete');

  const fetchProjectsData = async () => {
    const data = await getProjects();
    setProjects(data?.projects || []);
    setDepartments(data?.departments || []);
  };
  const fetchPlantRates = async (projectId) => setPlantRates(projectId ? (await getPlantRatesByProjectId(projectId)) || [] : []);

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
    fetchProjectsData();
    getCurrency().then(setCurrencies);
    getGroups().then(setGroups);
    getMeasurementUnits().then((units) => setMeasurementUnits((units || [])));
    getClass().then(setClasses);
    getManufacturer().then(setManufacturers);
    getCustomers().then(setCustomers);
    getProjectStatus().then(setProjectStatuses);
    getTyreTypes().then((data) => setTyreTypes(data || []));
    getExpenseCodes().then(setExpenseCodes);
    getPlantRateGlobals().then((data) => setPlantRateGlobals(data || []));
    getPlantRateFluidList().then((data) => setFluidItems(data || []));
  }, []);

  const openCreateProjectModal = () => {
    const globalDefaults = plantRateGlobals?.[0] || {};
    setProjectForm({
      currencyId: 1,
      status: 3,
      interestRate: Number(globalDefaults.interestRate || 0),
      insuranceRate: Number(globalDefaults.insuranceRate || 0),
      fuelPrice: Number(globalDefaults.fuelPrice || 0),
      technicianRate: Number(globalDefaults.technicianRate || 0),
      operatorRate: Number(globalDefaults.operatorRate || 0),
    });
    setProjectSettingsErrors({});
    setSelectedProject(null);
    setShowProjectModal(true);
  };

  const formatTyreTypeLabel = (tyreType) => {
    if (!tyreType) return '';
    return [tyreType.manufacturer, tyreType.size, tyreType.traCode, tyreType.treadDesign, Number(tyreType.plyRating) !== 0 ? tyreType.plyRating : null]
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
      .join(' ');
  };

  const tyreTypeOptions = useMemo(() => tyreTypes.map((t) => ({ value: t.id, label: formatTyreTypeLabel(t), raw: t })), [tyreTypes]);

  const tyreSetupFields = useMemo(() => ([
    { field: 'tyreTypeId', label: 'Tyre Type', type: 'select', options: tyreTypeOptions, size: '12' },
    { field: 'name', label: 'Name', type: 'text', size: '12', disabled: true },
    { field: 'tyreCost', label: 'Tyre Cost (ea)', type: 'number', size: '4', mustBePositiveDecimal: true, allowZeroValue: true },
    { field: 'numberOfTyres', label: 'Number of Tyres', type: 'number', size: '4', mustBePositiveInteger: true },
    { field: 'tyreLife', label: 'Tyre Life', type: 'number', size: '4', mustBePositiveInteger: true },
    { field: 'rimCost', label: 'Rim Cost (ea)', type: 'number', size: '4', mustBePositiveDecimal: true, allowZeroValue: true },
    { field: 'rimLife', label: 'Rim Life', type: 'number', size: '4', mustBePositiveInteger: true, allowZeroValue: true },
    { field: 'notes', label: 'Notes', type: 'textarea', size: '12' },
  ]), [tyreTypeOptions]);
  const { errors: tyreSetupErrors } = useValidator(tyreSetupFields, tyreSetupForm);
  const { renderField: renderTyreSetupField } = useFormBuilder(tyreSetupFields, tyreSetupForm, setTyreSetupForm, tyreSetupErrors);

  const openTyreSetupModal = async () => {
    if (!editingPlantRate?.id) return;
    const allTyres = await getPlantRateTyres();
    const current = (allTyres || []).find((row) => Number(row.template) === 0 && Number(row.plantRateId) === Number(editingPlantRate.id));
    setEditingPlantRateTyre(current || null);
    setTyreSetupForm(current ? { ...current } : { plantRateId: editingPlantRate.id, template: 0, includeRims: 0 });
    setShowTyreSetupModal(true);
  };

  const tableFilters = useMemo(() => [
    { field: 'groupId', type: 'dropDown', label: 'Group', api: 'groups', displayField: (g) => `${g.manufacturer?.manufacturerName || ''} ${g.groupName || ''} ${g.class?.className || ''}`.trim(), menuBarFilter: 1 },
    { field: 'measurementUnitsId', type: 'dropDown', label: 'Measurement Unit', api: 'measurement-units', displayField: 'unitName', menuBarFilter: 1 },
  ], []);
  const { appliedFilters, renderFilterControls } = useTableFilters(tableFilters);

  const filteredPlantRates = useMemo(() => plantRates.filter((r) => {
    if (appliedFilters.groupId && Number(r.groupId) !== Number(appliedFilters.groupId)) return false;
    if (appliedFilters.measurementUnitsId && Number(r.measurementUnitsId) !== Number(appliedFilters.measurementUnitsId)) return false;
    return true;
  }), [plantRates, appliedFilters]);
  const groupedProjects = useMemo(() => {
    const visibleProjects = projects.filter((p) => (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()));
    const groupsMap = visibleProjects.reduce((acc, project) => {
      const customerLabel = project.customer?.name || 'Unassigned Customer';
      const key = String(project.customer?.id || `unassigned-${customerLabel}`);
      if (!acc[key]) acc[key] = { key, label: customerLabel, items: [] };
      acc[key].items.push(project);
      return acc;
    }, {});
    return Object.values(groupsMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [projects, searchTerm]);

  useEffect(() => {
    setOpenProjectGroupKeys((prev) => {
      const next = { ...prev };
      groupedProjects.forEach((group) => {
        if (!(group.key in next)) next[group.key] = true;
      });
      return next;
    });
  }, [groupedProjects]);

  const groupOptions = useMemo(() => [
    ...groups.map((g) => ({ value: g.id, label: `${g.manufacturer?.manufacturerName || g.manufacturer?.manufacturer || ''} ${g.groupName || ''} ${g.class?.className || ''}`.replace(/\s+/g, ' ').trim(), raw: g })),
    { value: '__add_group__', label: '+ Add Group' },
  ], [groups]);

  const unitOptions = useMemo(() => measurementUnits.filter((unit) => Number(unit.isUtilisation) === 1).map((m) => ({ value: m.id, label: m.unitName })), [measurementUnits]);
  const currencyOptions = useMemo(() => currencies.map((c) => ({ value: c.id, label: `${c.name || c.currency || c.code || ''} (${c.symbol || ''})`.trim() })), [currencies]);
  const customerOptions = useMemo(() => customers.map((c) => ({ value: c.id, label: c.name })), [customers]);
  const statusOptions = useMemo(() => projectStatuses.map((s) => ({ value: s.id, label: s.name })), [projectStatuses]);
  const departmentOptions = useMemo(() => (departments || []).map((d) => ({ value: d.id, label: d.name || d.departmentName || d.description || `ID ${d.id}`, parentId: d.parentId ?? d.parent?.id ?? null, parentName: d.parent?.name ?? d.parentName ?? '' })), [departments]);

  const projectSettingsFields = useMemo(() => ([
    { field: 'customerId', label: 'Customer', type: 'select', options: customerOptions, size: '6', required: true },
    { field: 'name', label: 'Name', type: 'text', size: '6', required: true },
    { field: 'status', label: 'Status', type: 'select', options: statusOptions, size: '3', required: true },
    { field: 'description', label: 'Description', type: 'text', size: '9' },
    { field: 'currencyId', label: 'Currency', type: 'select', size: '4', required: true, options: currencies.map((c) => ({ value: c.id, label: c.name || c.currency || c.code })) },
    { field: 'interestRate', label: 'Interest Rate', type: 'number', size: '4', required: true, mustBePositiveDecimal: true },
    { field: 'insuranceRate', label: 'Insurance Rate', type: 'number', size: '4', required: true, mustBePositiveDecimal: true },
    { field: 'fuelPrice', label: 'Fuel Price per Litre', type: 'number', size: '4', required: true, mustBePositiveDecimal: true },
    { field: 'technicianRate', label: 'Technician Rate per Hour', type: 'number', size: '4', required: true, mustBePositiveDecimal: true },
    { field: 'operatorRate', label: 'Operator Rate per Hour', type: 'number', size: '4', required: true, mustBePositiveDecimal: true },

  ]), [customerOptions, statusOptions, currencies]);
  const { errors: projectSettingsErrors, setErrors: setProjectSettingsErrors } = useValidator(projectSettingsFields, projectForm);
  const { renderField: renderProjectSettingsField } = useFormBuilder(projectSettingsFields, projectForm, setProjectForm, projectSettingsErrors);

  const formFields = useMemo(() => ({
    general: [
      { field: 'name', label: 'Name', type: 'text', size: '6' },
      { field: 'groupId', label: 'Group', type: 'select', size: '6', options: groupOptions, required: true },
      { field: 'currencyId', label: 'Currency', type: 'select', size: '4', options: currencyOptions, required: true },
      { field: 'measurementUnitsId', label: 'Measurement Units', type: 'select', size: '4', options: unitOptions, required: true },
      { field: 'template', label: 'Template', type: 'switch', size: '4' },
      { field: 'startUsage', label: 'Start Usage', type: 'number', size: '4' },
      { field: 'utilisation', label: 'Utilisation', type: 'number', size: '4', required: true },
      { field: 'endUsage', label: 'End Usage', type: 'number', size: '4', disabled: endMode === 'date' },
      { field: 'notes', label: 'Notes', type: 'textarea', size: '12' },
    ],
  }), [groupOptions, unitOptions, currencyOptions, endMode]);

  const { errors, setErrors, validate } = useValidator([formFields.general,], plantRateForm);

  const { renderField: renderGeneralField } = useFormBuilder(formFields.general, plantRateForm, setPlantRateForm, errors);

  const depreciationFields = useMemo(() => ([
    { field: 'depreciationBasis', label: 'Depreciation Method', type: 'select', size: '12', options: depreciationBasisOptions, required: true },
    { field: 'depreciationRate', label: 'Depreciation Rate', type: 'number', size: '6', mustBePositiveDecimal: true, condition: (f) => ['declining_balance_hours', 'declining_balance_time'].includes(f.depreciationBasis) },
    { field: 'depreciationEndHours', label: 'Depreciation End Hours', type: 'number', size: '6', mustBePositiveDecimal: true, disabled: Number(plantRateForm.depreciationDbProfile || 0) === 0 && plantRateForm.depreciationBasis === 'declining_balance_hours', condition: (f) => ['straight_line', 'declining_balance_hours'].includes(f.depreciationBasis) },
    { field: 'residualValue', label: 'Residual Value', type: 'number', size: '6', mustBePositiveDecimal: true, allowZeroValue: true, disabled: plantRateForm.depreciationBasis !== 'straight_line' },
    { field: 'depreciationDbProfile', label: 'Use Depreciation DB End Hours', type: 'switch', size: '6', condition: (f) => f.depreciationBasis === 'declining_balance_hours' },
    { field: 'depreciationMonths', label: 'Depreciation Months', type: 'number', size: '6', mustBePositiveDecimal: true, condition: (f) => f.depreciationBasis === 'declining_balance_time' },
  ]), [plantRateForm.depreciationBasis, plantRateForm.deprecationDbProfile]);

  const { errors: depreciationErrors, setErrors: setDepreciationErrors } = useValidator(depreciationFields, plantRateForm);

  const { renderField: renderDepreciationField } = useFormBuilder(depreciationFields, plantRateForm, setPlantRateForm, depreciationErrors);

  const interestMethodOptions = useMemo(() => ([
    { value: 'none', label: 'None' }, { value: 'flat_interest_purchase_price', label: 'Flat Interest on Purchase Price' }, { value: 'average_investment', label: 'Average Investment' }, { value: 'declining_balance_book_value', label: 'Declining Balance Book Value' }, { value: 'finance_payment', label: 'Finance Payment' }, { value: 'wacc', label: 'WACC' }, { value: 'opportunity_cost', label: 'Opportunity Cost' },
  ]), []);

  const interestFields = useMemo(() => ([
    { field: 'interestCalculationMethod', label: 'Interest Method', type: 'select', size: '12', options: interestMethodOptions, required: true },
    { field: 'interestRate', label: 'Interest Rate (%)', type: 'number', size: '6', mustBePositiveDecimal: true, allowZeroValue: true, condition: (f) => ['flat_interest_purchase_price', 'average_investment', 'declining_balance_book_value', 'finance_payment', 'opportunity_cost'].includes(f.interestCalculationMethod) },
    { field: 'numberOfMonths', label: 'Number of Months', type: 'number', size: '6', mustBePositiveInteger: true, condition: (f) => f.interestCalculationMethod === 'finance_payment' },
    { field: 'balloonPayment', label: 'Balloon Payment', type: 'number', size: '6', mustBePositiveDecimal: true, allowZeroValue: true, condition: (f) => f.interestCalculationMethod === 'finance_payment' },
    { field: 'waccDebtPercent', label: 'WACC Debt %', type: 'number', size: '6', mustBePositiveDecimal: true, allowZeroValue: true, condition: (f) => f.interestCalculationMethod === 'wacc' },
    { field: 'waccDebtRate', label: 'WACC Debt Rate', type: 'number', size: '6', mustBePositiveDecimal: true, allowZeroValue: true, condition: (f) => f.interestCalculationMethod === 'wacc' },
    { field: 'waccEquityPercent', label: 'WACC Equity %', type: 'number', size: '6', mustBePositiveDecimal: true, allowZeroValue: true, condition: (f) => f.interestCalculationMethod === 'wacc' },
    { field: 'waccEquityRate', label: 'WACC Equity Rate', type: 'number', size: '6', mustBePositiveDecimal: true, allowZeroValue: true, condition: (f) => f.interestCalculationMethod === 'wacc' },
  ]), [interestMethodOptions]);

  const { errors: interestErrors } = useValidator(interestFields, plantRateForm);

  const { renderField: renderInterestField } = useFormBuilder(interestFields, plantRateForm, setPlantRateForm, interestErrors);

  const fuelMeasurementOptions = useMemo(() => measurementUnits
    .filter((unit) => Number(unit.isUtilisation) !== 1)
    .map((unit) => ({ value: unit.id, label: `${unit.unitName} (${unit.unitCode})` })), [measurementUnits]);

  const fuelUsageFields = useMemo(() => ([
    { field: 'name', label: 'Profile Name', type: 'text', size: '6', required: true },
    { field: 'price', label: 'Price (From project settings)', type: 'number', size: '6', mustBePositiveDecimal: true, allowZeroValue: true, disabled: true },
    { field: 'value', label: `Fuel Consumption per ${editingPlantRate?.measurementUnits?.unitName || 'Hour'}`, type: 'number', size: '4', required: true, mustBePositiveDecimal: true, allowZeroValue: true },
    { field: 'measurementUnitId', label: 'Units', type: 'select', size: '4', options: fuelMeasurementOptions, required: true },
    { field: 'type', label: 'Fuel Type', type: 'select', size: '4', options: [{ value: '1', label: 'Diesel' }, { value: '2', label: 'Petrol' }] },
  ]), [fuelMeasurementOptions, editingPlantRate?.measurementUnits?.unitName]);

  const { errors: fuelUsageErrors, setErrors: setFuelUsageErrors } = useValidator(fuelUsageFields, fuelUsageForm);

  const { renderField: renderFuelUsageField } = useFormBuilder(fuelUsageFields, fuelUsageForm, setFuelUsageForm, fuelUsageErrors);

  const endConstraintField = useMemo(() => [{ field: 'endConstraint', label: 'End Constraint', type: 'select', size: '6', options: [{ value: 'usage', label: 'End Usage' }, { value: 'date', label: 'End Date' }], required: true }], []);

  const { renderField: renderEndConstraintField } = useFormBuilder(endConstraintField, { endConstraint: endMode }, (u) => {
    const next = typeof u === 'function' ? u({ endConstraint: endMode }) : u;
    setEndMode(next.endConstraint || 'usage');
  }, errors);

  const savePlantRate = async () => {
    const payload = { ...plantRateForm, projectId: selectedProject?.id };

    if (!payload.name?.trim()) {
      const selectedGroup = groups.find((g) => Number(g.id) === Number(payload.groupId));
      if (selectedGroup?.groupName) payload.name = selectedGroup.groupName;
    }

    if (!payload.name?.trim()) return alert('Name is required');

    if (!validate()) return;

    if (Number(payload.startUsage) < 0) return alert('Start usage must be non-negative');

    if (Number(payload.utilisation) <= 0) return alert('Utilisation must be greater than 0');

    if (endMode === 'usage') {
      setErrors((prev) => {
        if (!prev.endUsage) return prev;
        const next = { ...prev };
        delete next.endUsage;
        return next;
      });
      const endUsageValue = Number(payload.endUsage);
      const startUsageValue = Number(payload.startUsage);
      if (!Number.isFinite(endUsageValue) || endUsageValue === 0 || endUsageValue <= startUsageValue) {
        setErrors((prev) => ({ ...prev, endUsage: 'End Usage must be greater than Start Usage and greater than zero' }));
        return;
      }
    }

    if ((payload.purchasePrice ?? 0) < 0 || (payload.residualValue ?? 0) < 0 || (payload.depreciationRate ?? 0) < 0) return alert('Financial values must be non-negative');

    if (payload.interestRate < 0 || payload.interestRate >= 50 || payload.insuranceRate < 0 || payload.insuranceRate >= 50) return alert('Interest and insurance rates must be between 0 and < 50');

    if (!editingPlantRate) {
      const createdPayload = {
        ...payload,
        depreciationRate: 15.00,
        depreciationMonths: calculateDefaultDepreciationMonths(payload.startUsage, payload.endUsage),
        depreciationEndHours: Number(payload.endUsage || 0),
        createdBy: userProfile?.id
      };

      const createdPlantRate = await createPlantRates(createdPayload);

      setShowPlantRateModal(false);
      await fetchPlantRates(selectedProject?.id);

      if (createdPlantRate?.id) {
        const refreshed = await getPlantRatesByProjectId(selectedProject?.id);
        const newRate = (refreshed || []).find((r) => Number(r.id) === Number(createdPlantRate.id));
        if (newRate) {
          setEditingPlantRate(newRate);
          setPlantRateForm(newRate);
          setEndMode(newRate.endDate ? 'date' : 'usage');
          setErrors({});
          setShowEditPlantRateModal(true);
        }
      }
      return;
    }

    await updatePlantRates(editingPlantRate.id, { ...payload, updatedBy: userProfile?.id, updatedAt: new Date().toISOString() });
    setShowPlantRateModal(false);
    setEditingPlantRate(null);
    await fetchPlantRates(selectedProject?.id);
  };

  useEffect(() => {
    if (plantRateForm.groupId === '__add_group__') {
      setShowAddGroupModal(true);
      setPlantRateForm((prev) => ({ ...prev, groupId: '' }));
    }
  }, [plantRateForm.groupId]);

  const addGroupFields = useMemo(() => [
    { field: 'groupName', label: 'Model or Group Name', type: 'text', size: 12, required: true },
    { field: 'classId', label: 'Class', type: 'select', size: 6, options: classes.map((c) => ({ value: c.id, label: c.className })), required: true },
    { field: 'manufacturerId', label: 'Manufacturer', type: 'select', size: 6, options: manufacturers.map((m) => ({ value: m.id, label: m.manufacturer || m.manufacturerName })), required: true },
  ], [classes, manufacturers]);

  const { errors: addGroupErrors, setErrors: setAddGroupErrors, validate: validateAddGroup } = useValidator(addGroupFields, newGroup);

  const { renderField: renderAddGroupField } = useFormBuilder(addGroupFields, newGroup, setNewGroup, addGroupErrors);

  const handleSaveGroup = async () => {
    if (!validateAddGroup()) return;
    const { exists } = await checkGroupNameExists({ groupName: newGroup.groupName });
    if (exists) {
      setAddGroupErrors((prev) => ({ ...prev, groupName: 'Group name already exists' }));
      return;
    }
    const created = await createGroup(newGroup);
    const allGroups = await getGroups();
    setGroups(allGroups || []);
    const createdId = created?.id || allGroups?.find((g) => g.groupName === newGroup.groupName)?.id;
    if (createdId) setPlantRateForm((prev) => ({ ...prev, groupId: createdId }));
    setShowAddGroupModal(false);
    setNewGroup({});
  };

  const purchasePriceValidationFields = useMemo(
    () => (purchasePriceItems || []).map((item) => ({
      field: `cost_${item.id}`,
      label: item.name || `Item ${item.id}`,
      mustBePositiveDecimal: true,
      allowZeroValue: true,
    })),
    [purchasePriceItems],
  );
  const purchasePriceValidationData = useMemo(
    () => (purchasePriceItems || []).reduce((acc, item) => ({ ...acc, [`cost_${item.id}`]: item.cost ?? '' }), {}),
    [purchasePriceItems],
  );
  const { validate: validatePurchasePriceItems } = useValidator(purchasePriceValidationFields, purchasePriceValidationData);

  const purchasePriceFromItems = useMemo(
    () => (purchasePriceItems || []).reduce((sum, item) => sum + (Number(item.cost || 0) * Number(item.currencyRate || 1)), 0),
    [purchasePriceItems],
  );

  useEffect(() => {
    const currentRateId = Number(editingPlantRate?.id);
    const loadedRateId = Number(purchasePriceItemsRateId);
    const hasLoadedItemsForCurrentRate = Number.isFinite(currentRateId)
      && Number.isFinite(loadedRateId)
      && loadedRateId === currentRateId;

    if (Number(plantRateForm.overridePurchase) === 0 && hasLoadedItemsForCurrentRate) {
      setPlantRateForm((prev) => ({ ...prev, purchasePrice: Number.isFinite(purchasePriceFromItems) ? String(Math.round(purchasePriceFromItems)) : prev.purchasePrice }));
    }
  }, [purchasePriceFromItems, plantRateForm.overridePurchase, editingPlantRate?.id, purchasePriceItemsRateId]);


  useEffect(() => {
    if (!showEditPlantRateModal) {
      setPurchasePriceItems([]);
      setPurchasePriceItemsRateId(null);
    }
  }, [showEditPlantRateModal]);

  const openPurchasePriceModal = async () => {
    if (!editingPlantRate?.id) return;
    const allItems = await getPurchasePriceItems();
    const rateItems = (allItems || []).filter((item) => Number(item.plantRateId) === Number(editingPlantRate.id));
    setPurchasePriceItems(rateItems);
    setPurchasePriceItemsRateId(Number(editingPlantRate.id));
    setPurchasePriceItemErrors({});
    setShowPurchasePriceModal(true);
  };

  const savePurchasePriceModal = async () => {
    if (Number(plantRateForm.overridePurchase) === 0) {
      const isValid = validatePurchasePriceItems();
      if (!isValid) {
        const nextErrors = {};
        (purchasePriceItems || []).forEach((item) => {
          const value = Number(item.cost);
          if (!Number.isFinite(value) || value < 0) nextErrors[item.id] = 'Cost must be zero or a positive number';
        });
        setPurchasePriceItemErrors(nextErrors);
        return;
      }
    }
    await Promise.all((purchasePriceItems || []).map((item) => updatePurchasePriceItems(item.id, {
      cost: Number(item.cost || 0),
      currencyId: Number(item.currencyId || 1),
      currencyRate: Number(item.currencyId) === 1 ? 1 : Number(item.currencyRate || 1),
    })));
    const purchasePriceToSave = Number(plantRateForm.overridePurchase) === 1
      ? Number(plantRateForm.purchasePrice || 0)
      : Number(purchasePriceFromItems || 0);
    setPlantRateForm((prev) => ({ ...prev, purchasePrice: String(Math.round(purchasePriceToSave)) }));
    if (editingPlantRate?.id) {
      await updatePlantRates(editingPlantRate.id, {
        purchasePrice: purchasePriceToSave,
        overridePurchase: Number(plantRateForm.overridePurchase) === 1 ? 1 : 0,
        updatedBy: userProfile?.id,
        updatedAt: new Date().toISOString(),
      });
    }
    setShowPurchasePriceModal(false);
  };

  const typeValidators = {
    numberOfTyres: (v) => Number.isInteger(Number(v || 0)) && Number(v || 0) > 0,
    tyreLife: (v) => Number.isInteger(Number(v || 0)) && Number(v || 0) > 0,
    rimLife: (v) => Number.isInteger(Number(v || 0)) && Number(v || 0) >= 0,
  };

  const persistPlantRateFieldOnBlur = async (field, rawValue) => {
    const value = rawValue ?? '';
    const editingPlantRateId = editingPlantRate?.id;
    const baselineValue = editingPlantRate?.[field] ?? '';
    if (!editingPlantRateId) return;
    if (!dirtyPlantRateFields[field]) return;

    if (typeValidators[field] && !typeValidators[field](value)) return;
    if (String(baselineValue) === String(value)) {
      setDirtyPlantRateFields((prev) => ({ ...prev, [field]: false }));
      return;
    }

    try {
      await updatePlantRates(editingPlantRateId, {
        [field]: value,
        updatedBy: userProfile?.id,
        updatedAt: new Date().toISOString(),
      });

      setEditingPlantRate((prev) => (prev && Number(prev.id) === Number(editingPlantRateId) ? { ...prev, [field]: value } : prev));
      setDirtyPlantRateFields((prev) => ({ ...prev, [field]: false }));
    } catch (_error) {
      setDirtyPlantRateFields((prev) => ({ ...prev, [field]: true }));
    }
  };

  const renderEditField = (label, field, { type = 'number', placeholder = '', withIcon = false, options = [], disabled = false, italicPlaceholder = false, noValueText = '' } = {}) => {
    const value = editInputDraft?.[field] ?? plantRateForm?.[field] ?? '';
    return (
      <div className="mb-12">
        <label className="form-label fw-medium">{label}</label>
        <div className="d-flex gap-8 align-items-center">
          {type === 'select' ? (
            <select
              className="form-select"
              value={value}
              disabled={disabled}
              onChange={(e) => { const nextValue = e.target.value; setEditInputDraft((prev) => ({ ...prev, [field]: nextValue })); setPlantRateForm((prev) => ({ ...prev, [field]: nextValue })); setDirtyPlantRateFields((prev) => ({ ...prev, [field]: String(editingPlantRate?.[field] ?? '') !== String(nextValue) })); }}
              onBlur={(e) => persistPlantRateFieldOnBlur(field, e.target.value)}
            >
              <option value="">Select...</option>
              {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          ) : (
            <input
              type={type}
              className="form-control" style={italicPlaceholder && !value ? { fontStyle: 'italic' } : undefined}
              placeholder={noValueText || placeholder}
              value={value}
              disabled={disabled}
              onChange={(e) => { const nextValue = e.target.value; setEditInputDraft((prev) => ({ ...prev, [field]: nextValue })); setPlantRateForm((prev) => ({ ...prev, [field]: nextValue })); setDirtyPlantRateFields((prev) => ({ ...prev, [field]: String(editingPlantRate?.[field] ?? '') !== String(nextValue) })); }}
              onBlur={(e) => persistPlantRateFieldOnBlur(field, e.target.value)}
            />
          )}
          {withIcon && (
            <button
              className="btn btn-outline-primary px-12 py-6" type="button"
              onClick={
                field === 'purchasePrice' ? openPurchasePriceModal : (
                  field === 'tyres' ? openTyreSetupModal :
                    (field === 'fuelUsage' ? openFuelUsageModal :
                      (field === 'fluids' ? openOilFluidsModal :
                        (field === 'maintenanceTasks' ? () => setShowMaintenanceTasksModal(true) :
                        (field === 'groundTools' ? openGetSetupModal :
                          (field === 'depreciationBasis' ? () => setShowDepreciationModal(true) :
                            (field === 'interestMonthlyAmount' ? () => setShowInterestModal(true) :
                              (field === 'insuranceMonthlyAmount' ? () => setShowInsuranceModal(true) :
                                (field === 'overheads' ? () => setShowOverheadSetupModal(true) :
                                  (field === 'minimumMonthlyCharge' ? () => {
                                    setMinimumMonthlyChargeErrors({});
                                    setMinimumMonthlyChargeForm({
                                      minimumBasis: plantRateForm.minimumBasis || 'hours',
                                      minimumHours: Number(plantRateForm.minimumHours || 0),
                                      minimumCharge: Number(plantRateForm.minimumCharge || 0),
                                    });
                                    setShowMinimumMonthlyChargeModal(true);
                                  } :
                                  (field === 'marginFixed' ? () => {
                                    setMarginSetupForm({
                                      marginMethod: plantRateForm.marginMethod || 'percent',
                                      marginPercent: Number(plantRateForm.marginPercent || 0),
                                      marginFixed: Number(plantRateForm.marginFixed || 0),
                                    });
                                    setShowMarginSetupModal(true);
                                  } :
                                    undefined)))))))))))}>
              <Icon icon="mdi:dots-horizontal" />
            </button>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const selectedType = tyreTypes.find((t) => Number(t.id) === Number(tyreSetupForm.tyreTypeId));
    if (!selectedType) return;
    const nextName = formatTyreTypeLabel(selectedType);
    setTyreSetupForm((prev) => ({
      ...prev,
      name: nextName,
      tyreCost: Number(selectedType.price || 0),
      tyreTypeImage: selectedType.tyreImage || null,
    }));
  }, [tyreSetupForm.tyreTypeId]);

  const saveTyreSetup = async () => {
    const validationFields = Number(tyreSetupForm.includeRims) === 1 ? tyreSetupFields : tyreSetupFields.filter((f) => !['rimLife', 'rimCost'].includes(f.field));
    const validationErrors = validateForm(validationFields, tyreSetupForm);
    if (Object.keys(validationErrors).length) return;
    const payload = {
      ...tyreSetupForm,
      plantRateId: editingPlantRate?.id,
      template: 0,
      includeRims: Number(tyreSetupForm.includeRims) === 1 ? 1 : 0,
    };

    delete payload.tyreTypeImage;
    if (editingPlantRateTyre?.id) await updatePlantRateTyres(editingPlantRateTyre.id, payload);
    else await createPlantRateTyres(payload);
    await refreshPlantRateTable(editingPlantRate?.id);
    setShowTyreSetupModal(false);
  };
  const openFuelUsageModal = async () => {
    if (!editingPlantRate?.id) return;

    const [allFuelRows, groupProfiles] = await Promise.all([
      getPlantRateFuelTemplates(),
      editingPlantRate.groupId ? getFuelTemplatesByGroup(editingPlantRate.groupId) : Promise.resolve([]),
    ]);

    const selectedFuelRow = (allFuelRows || []).find((row) => Number(row.plantRateId) === Number(editingPlantRate.id));
    setEditingFuelTemplate(selectedFuelRow || null);
    setFuelUsageForm(selectedFuelRow ? {
      ...selectedFuelRow,
      price: Number(selectedProject?.fuelPrice || 0),
    } :
      {
        plantRateId: editingPlantRate.id,
        groupId: editingPlantRate.groupId || null,
        name: editingPlantRate.name || '',
        price: Number(selectedProject?.fuelPrice || 0),
        value: plantRateForm.fuelUsage || 0, template: 0
      });
    setFuelTemplateProfiles(groupProfiles || []);
    setShowFuelUsageModal(true);
  };
  const applyFuelTemplateProfile = (templateId) => {
    const profile = fuelTemplateProfiles.find((row) => Number(row.id) === Number(templateId));
    if (!profile) return;
    setFuelUsageForm((prev) => ({ ...prev, name: profile.name || '', value: profile.value ?? 0 }));
  };
  const saveFuelUsageSetup = async () => {
    const validationErrors = validateForm(fuelUsageFields, fuelUsageForm);

    setFuelUsageErrors(validationErrors);

    if (Object.keys(validationErrors).length) return;

    const payload =
    {
      ...fuelUsageForm,
      price: Number(selectedProject?.fuelPrice || 0),
      plantRateId: editingPlantRate?.id,
      groupId: editingPlantRate?.groupId || null, template: 0
    };

    if (editingFuelTemplate?.id) await updatePlantRateFuelTemplates(editingFuelTemplate.id, payload);

    else await createPlantRateFuelTemplates(payload);

    const fuelUsage = Number(payload.value || 0);
    const fuelLine = Number(selectedProject?.fuelPrice || 0) * fuelUsage;
    setPlantRateForm((prev) => ({ ...prev, fuelUsage, fuelType: payload.type, fuelHourlyCost: fuelLine }));
    await refreshPlantRateTable(editingPlantRate?.id);
    setShowFuelUsageModal(false);
  };


  const fluidItemOptions = useMemo(() =>
    (fluidItems || []).map((i) =>
    ({
      value: i.id,
      label: `${i?.manufacturer?.manufacturer || ''} ${i.name || ''} ${i.fluidType || ''}`.replace(/\s+/g, ' ').trim()
    })),
    [fluidItems]);

  const fluidFields = useMemo(() => ([
    { field: 'fluidItemId', label: 'Fluid Item', type: 'select', size: '12', options: fluidItemOptions },
    // { field: 'partNumber', label: 'Part Number', type: 'text', size: '6' },
    // { field: 'name', label: 'Name', type: 'text', size: '6' },
    { field: 'compartment', label: 'Compartment', type: 'text', size: '4', required: true },

    { field: 'quantity', label: 'Quantity', type: 'number', size: '4', mustBePositiveDecimal: true, allowZeroValue: true },
    { field: 'life', label: 'Life', type: 'number', size: '4', required: true, min: 1 },
    { field: 'cost', label: 'Cost', type: 'number', size: '4', mustBePositiveDecimal: true, allowZeroValue: true },
    { field: 'currencyId', label: 'Currency', type: 'select', size: '4', required: true, options: currencyOptions },

    { field: 'topUpPercent', label: 'Top Up Percent', type: 'number', size: '4', mustBePositiveDecimal: true, allowZeroValue: true },

  ]), [currencyOptions, fluidItemOptions]);
  const { errors: fluidErrors, setErrors: setFluidErrors } = useValidator(fluidFields, fluidForm);
  const { renderField: renderFluidField } = useFormBuilder(fluidFields, fluidForm, setFluidForm, fluidErrors);

  const getFields = useMemo(() => ([
    { field: 'partNumber', label: 'Part Number', type: 'text', size: '6' },
    { field: 'name', label: 'Name', type: 'text', size: '6' },
    { field: 'life', label: 'Life', type: 'number', size: '4', required: true, mustBePositiveInteger: true },
    { field: 'cost', label: 'Cost', type: 'number', size: '4', mustBePositiveDecimal: true, allowZeroValue: true },
    { field: 'currencyId', label: 'Currency', type: 'select', size: '4', required: true, options: currencies.map((c) => ({ value: c.id, label: c.symbol || c.currencyName })) },
    { field: 'quantity', label: 'Quantity', type: 'number', size: '4', mustBePositiveDecimal: true, allowZeroValue: true },
  ]), [currencies]);
  const { errors: getErrors, setErrors: setGetErrors } = useValidator(getFields, getForm);
  const { renderField: renderGetField } = useFormBuilder(getFields, getForm, setGetForm, getErrors);

  const openGetSetupModal = async () => {
    if (!editingPlantRate?.id) return;
    setPlantRateGets(await getPlantRateGetByPlantRateId(editingPlantRate.id));
    const templates = await getPlantRateGetListTemplates();
    setHasGetTemplatesForGroup((templates || []).some((row) => Number(row.groupId) === Number(editingPlantRate.groupId)));
    setShowGetSetupModal(true);
  };

  const saveGet = async () => {
    const validationErrors = validateForm(getFields, getForm);
    if (Number(getForm.life || 0) <= 0) validationErrors.life = 'Life must be greater than 0';
    setGetErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;
    const payload = {
      ...getForm,
      plantRateId: editingPlantRate.id,
      groupId: editingPlantRate.groupId,
      template: 0,
      life: Number(getForm.life || 0),
      cost: Number(getForm.cost || 0),
      currencyId: Number(getForm.currencyId || 1),
      quantity: Number(getForm.quantity || 0),
    };
    if (editingGet?.id) await updatePlantRateGetList(editingGet.id, payload);
    else await createPlantRateGetList(payload);
    setShowGetEditModal(false);
    setPlantRateGets(await getPlantRateGetByPlantRateId(editingPlantRate.id));
    await refreshPlantRateTable(editingPlantRate?.id);
  };

  const openOilFluidsModal = async () => {
    if (!editingPlantRate?.id) return;
    setPlantRateFluids(await getPlantRateFluidsByPlantRateId(editingPlantRate.id));
    const allFluids = await getPlantRateFluids();
    setFluidTemplatesForGroup((allFluids || []).filter((row) => Number(row.template) === 1 && Number(row.groupId) === Number(editingPlantRate.groupId)));
    setShowOilFluidsModal(true);
  };
  const saveFluid = async () => {
    const validationErrors = validateForm(fluidFields, fluidForm);
    if (Number(fluidForm.life || 0) <= 0) validationErrors.life = 'Life must be greater than 0';
    setFluidErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;
    const payload = { ...fluidForm, life: Number(fluidForm.life || 0), cost: Number(fluidForm.cost || 0), quantity: Number(fluidForm.quantity || 0), topUpPercent: Number(fluidForm.topUpPercent || 0), currencyId: Number(fluidForm.currencyId || 1), plantRateId: editingPlantRate.id, groupId: editingPlantRate.groupId || null, template: 0 };
    if (editingFluid?.id) await updatePlantRateFluids(editingFluid.id, payload); else await createPlantRateFluids(payload);
    setShowFluidEditModal(false); setEditingFluid(null); setFluidForm({});
    setPlantRateFluids(await getPlantRateFluidsByPlantRateId(editingPlantRate.id));
    await refreshPlantRateTable(editingPlantRate?.id);
  };

  const openProjectSettingsModal = () => {
    if (!selectedProject) return;
    setProjectSettingsErrors({});
    setProjectForm({
      ...selectedProject,
      customerId: Number(selectedProject.customerId || selectedProject.customer?.id || 0) || '',
      status: Number(selectedProject.status || selectedProject.statusId || selectedProject.projectStatus?.id || 0) || '',
      interestRate: Number(selectedProject.interestRate || 0),
      insuranceRate: Number(selectedProject.insuranceRate || 0),
      technicianRate: Number(selectedProject.technicianRate || 0),
      operatorRate: Number(selectedProject.operatorRate || 0),
      fuelPrice: Number(selectedProject.fuelPrice || 0),
      currencyId: Number(selectedProject.currencyId || selectedProject.currency?.id || 1),
    });
    setShowProjectModal(true);
  };

  const maintenanceTaskFields = useMemo(() => ([
    { field: 'overrideMaintenance', label: 'Override Maintenance', type: 'switch', size: '4' },
    { field: 'maintenanceValue', label: 'Maintenance Value', type: 'number', size: '8', mustBePositiveDecimal: true, required: Number(plantRateForm.overrideMaintenance) === 1, disabled: Number(plantRateForm.overrideMaintenance) !== 1 },
  ]), [plantRateForm.overrideMaintenance]);
  const { renderField: renderMaintenanceField } = useFormBuilder(maintenanceTaskFields, plantRateForm, setPlantRateForm, maintenanceTaskErrors);
  const filteredMaintenanceTasks = useMemo(() => maintenanceTasks.filter((task) => `${task.taskName || ''} ${task.code?.code || ''}`.toLowerCase().includes(maintenanceTaskSearch.toLowerCase())), [maintenanceTaskSearch, maintenanceTasks]);

  useEffect(() => {
    if (!showMaintenanceTasksModal || !editingPlantRate?.id) return;
    const plantRateTasks =  getTasksByPlantRate(editingPlantRate.id)
    getTasksByPlantRate(editingPlantRate.id).then((rows) => setMaintenanceTasks(rows || []));
  }, [showMaintenanceTasksModal, editingPlantRate?.id]);

  useEffect(() => {
    if (!showEditPlantRateModal) return;
    const fuelUsage = Number(plantRateForm.fuelUsage || 0);
    const fuelLine = Number(selectedProject?.fuelPrice || 0) * fuelUsage;
    setPlantRateForm((prev) => ({ ...prev, fuelHourlyCost: fuelLine }));
  }, [showEditPlantRateModal, selectedProject?.fuelPrice]);

  const saveDepreciationMethod = async () => {
    if (!editingPlantRate?.id) return;

    const visibleFields = depreciationFields.filter((field) => !field.condition || field.condition(plantRateForm));
    const validationErrors = validateForm(visibleFields, plantRateForm);
    setDepreciationErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const payload = {
      depreciationBasis: plantRateForm.depreciationBasis || 'none',
      depreciationRate: Number(plantRateForm.depreciationRate || 0),
      depreciationEndHours: Number((plantRateForm.depreciationBasis === 'declining_balance_hours' && Number(plantRateForm.depreciationDbProfile || 0) === 0) ? (plantRateForm.endUsage || 0) : (plantRateForm.depreciationEndHours || 0)),
      residualValue: Number(plantRateForm.residualValue || 0),
      depreciationDbUsage: Number(plantRateForm.depreciationDbUsage || 0),
      depreciationDbProfile: Number(plantRateForm.depreciationDbProfile || 0),
      depreciationMonths: Number(plantRateForm.depreciationMonths || 0),
      // depreciationRatePerUsage: Number(plantRateForm.depreciationRatePerUsage || 0),
      updatedBy: userProfile?.id,
      updatedAt: new Date().toISOString(),
    };

    await updatePlantRates(editingPlantRate.id, payload);
    setEditingPlantRate((prev) => (prev ? { ...prev, ...payload } : prev));
    setPlantRateForm((prev) => ({ ...prev, ...payload }));
    setShowDepreciationModal(false);
    await fetchPlantRates(selectedProject?.id);
    await refreshPlantRateTable(editingPlantRate?.id);
  };

  const plantRateInputGroups = useMemo(() => ([
    {
      key: 'general',
      label: 'General',
      fields: [
        ['Name', 'name', { type: 'text' }],
        ['Description', 'description', { type: 'textarea' }],
        ['Minimum Monthly Charge', 'minimumBasis', { type: 'text', withIcon: true, disabled: true }],
        ['Margin', 'marginMethod', { type: 'text', placeholder: plantRateForm.marginMethod, withIcon: true, disabled: true }],
        ['Overheads', 'overheads', { type: 'text', placeholder: `${Number(plantRateForm.overheadPercent || 0)}%`, withIcon: true, disabled: true }],
      ],
    },
    {
      key: 'utilisation',
      label: 'Utilisation',
      suffix: editingPlantRate?.measurementUnits?.unitName || '',
      fields: [
        ['Start Usage', 'startUsage'],
        ['Utilisation', 'utilisation'],
        ['End Usage', 'endUsage'],
      ],
    },
    {
      key: 'financial',
      label: 'Financial',
      suffix: editingPlantRate?.currency?.symbol || '',
      fields: [
        ['Purchase Price', 'purchasePrice', { withIcon: true }],
        ['Depreciation Method', 'depreciationBasis', { type: 'select', options: depreciationBasisOptions, withIcon: true }],
        ['Residual Value', 'residualValue', { disabled: true }],
        ['Interest Monthly Amount', 'interestMonthlyAmount', { withIcon: true }],
        ['Insurance Monthly Amount', 'insuranceMonthlyAmount', { withIcon: true, disabled: (plantRateForm.insuranceCalculationMethod || 'percent_purchase_price') !== 'fixed' }],
      ],
    },
    {
      key: 'maintenance',
      label: 'Maintenance',
      fields: [
        ['Maintenance Tasks', 'maintenanceTasks', { placeholder: 'Calculated from Task List', withIcon: true, type: 'text', disabled: true }],
        ['Breakdown Profile', 'breakdownProfile', { type: 'select', options: breakdownProfiles }],
        ['Breakdown Percent', 'breakdownPercent'],
      ],
    },
    {
      key: 'operating',
      label: 'Operating',
      fields: [
        ['Operator Rate', 'operatorRate', { disabled: true }],
        ['Tyres', 'tyres', { type: 'text', placeholder: editingPlantRateTyre ? `${editingPlantRateTyre.numberOfTyres || 0} x ${editingPlantRateTyre.name || ''}` : '', withIcon: true, disabled: true }],
        ['Ground Tools', 'groundTools', { type: 'text', placeholder: (plantRateGets || []).length > 0 ? 'List of Parts' : '', withIcon: true, disabled: true }],
        ['Oils & Fluids', 'fluids', { type: 'text', placeholder: (plantRateFluids || []).length > 0 ? 'List of Fluids by compartment' : '... no oils or fluids selected ...', withIcon: true, disabled: true }],
        ['Fuel Usage', 'fuelUsage', { type: 'text', placeholder: editingFuelTemplate?.name || '', withIcon: true, disabled: true }],
        ['Overheads', 'overheads', { type: 'text', placeholder: 'Percentage of Plant Rate', withIcon: true, disabled: true }],
      ],
    },
  ]), [editingPlantRate, editingPlantRateTyre, editingFuelTemplate, plantRateForm.marginMethod, plantRateFluids, plantRateGets]);

  useEffect(() => {
    setOpenPlantRateInputGroupKeys((prev) => {
      const next = { ...prev };
      plantRateInputGroups.forEach((group) => {
        if (!(group.key in next)) next[group.key] = group.key === 'utilisation';
      });
      return next;
    });
  }, [plantRateInputGroups]);

  useEffect(() => {
    if (showEditPlantRateModal) setEditInputDraft({});
  }, [showEditPlantRateModal, editingPlantRate?.id]);

  useEffect(() => {
    if (!showEditPlantRateModal) return;
    if (!selectedProject?.operatorRate) return;
    setPlantRateForm((prev) => ({ ...prev, operatorRate: selectedProject.operatorRate }));
  }, [showEditPlantRateModal, selectedProject?.operatorRate]);

  useEffect(() => {
    if (!editingPlantRate?.id) return;
    getPlantRateCategoriesByPlantRateId(editingPlantRate.id).then((rows) => {
      setPlantRateCategories(rows || []);
    });
    getPlantRateTyres().then((allTyres) => {
      const current = (allTyres || []).find((row) => Number(row.template) === 0 && Number(row.plantRateId) === Number(editingPlantRate.id));
      setEditingPlantRateTyre(current || null);
    });
    getPlantRateFuelTemplates().then((allFuelRows) => {
      const selectedFuelRow = (allFuelRows || []).find((row) => Number(row.plantRateId) === Number(editingPlantRate.id));
      setEditingFuelTemplate(selectedFuelRow || null);
    });
    getPlantRateFluidsByPlantRateId(editingPlantRate.id).then((rows) => setPlantRateFluids(rows || []));
    getPlantRateGetByPlantRateId(editingPlantRate.id).then((rows) => setPlantRateGets(rows || []));
  }, [editingPlantRate?.id, showPlantRateCategoriesModal]);

  const calculatePlanningOccurrencesForPlantRate = async (plantRateId) => {
    if (!plantRateId) return;

    try {
      const result = await calculatePlantRatePlanningOccurrences({ plantRateId });
      setPlanningOccurrencesResult(result || null);
    } catch (error) {
      setPlanningOccurrencesResult(null);
      console.error('Failed to calculate next planning occurrences', error);
    }
  };

  const refreshPlantRateTable = async (plantRateId) => {
    if (!plantRateId) return;
    try {
      const result = await calculatePlantRateTable({ plantRateId });
      setPlantRateTableValues(result || {});
      await calculatePlanningOccurrencesForPlantRate(plantRateId);
    } catch {
      setPlantRateTableValues({});
    }
  };


  useEffect(() => {
    const run = async () => {
      if (!showEditPlantRateModal || !editingPlantRate?.id) return;
      await refreshPlantRateTable(editingPlantRate.id);
    };
    run();
  }, [showEditPlantRateModal, editingPlantRate?.id, plantRateCategories, plantRateFluids, plantRateGets]);

  const plantRateTableRows = useMemo(() => {
    const includedCategories = (plantRateCategories || []).filter((category) => Number(category.include) === 1);
    const rowsWithMeta = includedCategories.map((category) => {
      const normalizedCode = String(category.code || '').trim().toUpperCase();
      const resolvedCode = normalizedCode;
      return {
        code: category.code,
        value: Number(plantRateTableValues?.[resolvedCode] || 0),
        label: category.name,
        expense: category.expenseCode?.name || '',
      };
    });
    const total = rowsWithMeta.reduce((sum, r) => sum + Number(r.value || 0), 0);
    return { rows: rowsWithMeta, total };
  }, [plantRateCategories, plantRateTableValues]);

  useEffect(() => {
    if (!showOverheadSetupModal) return;
    setOverheadSetupForm({ overheadPercent: Number(plantRateForm.overheadPercent || 0) });
  }, [showOverheadSetupModal, plantRateForm.overheadPercent]);

  const saveOverheadSetup = async () => {
    if (!editingPlantRate?.id) return;
    const overheadPercent = Number(overheadSetupForm.overheadPercent || 0);
    await updatePlantRates(editingPlantRate.id, {
      overheadPercent,
      updatedBy: userProfile?.id,
      updatedAt: new Date().toISOString(),
    });
    setPlantRateForm((prev) => ({ ...prev, overheadPercent }));
    setEditingPlantRate((prev) => (prev ? { ...prev, overheadPercent } : prev));
    setShowOverheadSetupModal(false);
  };


  const saveInsuranceMethod = async () => {
    if (!editingPlantRate?.id) return;
    const payload = {
      insuranceRate: Number(plantRateForm.insuranceRate || 0),
      insuranceMonthlyAmount: Number(plantRateForm.insuranceMonthlyAmount || 0),
      insuranceCalculationMethod: plantRateForm.insuranceCalculationMethod || 'percent_purchase_price',
      updatedBy: userProfile?.id,
      updatedAt: new Date().toISOString(),
    };
    await updatePlantRates(editingPlantRate.id, payload);
    setPlantRateForm((prev) => ({ ...prev, ...payload }));
    setEditingPlantRate((prev) => (prev ? { ...prev, ...payload } : prev));
    setShowInsuranceModal(false);
    await refreshPlantRateTable(editingPlantRate?.id);
  };

  const saveInterestMethod = async () => {
    if (!editingPlantRate?.id) return;
    const payload = {
      interestHourlyAmount: Number(plantRateForm.interestHourlyAmount || 0),
      interestAnnualAmount: Number(plantRateForm.interestAnnualAmount || 0),
      interestCalculationMethod: plantRateForm.interestCalculationMethod || 'average_investment',
      interestAverageInvestment: Number(plantRateForm.interestAverageInvestment || 0),
      balloonPayment: Number(plantRateForm.balloonPayment || 0),
      effectiveInterestRate: Number(plantRateForm.effectiveInterestRate || 0),
      interestRate: Number(plantRateForm.interestRate || 0),
      updatedBy: userProfile?.id,
      updatedAt: new Date().toISOString(),
    };
    await updatePlantRates(editingPlantRate.id, payload);
    setPlantRateForm((prev) => ({ ...prev, ...payload }));
    setEditingPlantRate((prev) => (prev ? { ...prev, ...payload } : prev));
    setShowInterestModal(false);
    await refreshPlantRateTable(editingPlantRate?.id);
  };

  const saveMarginSetup = async () => {
    if (!editingPlantRate?.id) return;
    const payload = {
      marginMethod: marginSetupForm.marginMethod || 'percent',
      marginPercent: Number(marginSetupForm.marginPercent || 0),
      marginFixed: Number(marginSetupForm.marginFixed || 0),
      updatedBy: userProfile?.id,
      updatedAt: new Date().toISOString(),
    };
    await updatePlantRates(editingPlantRate.id, payload);
    setPlantRateForm((prev) => ({ ...prev, ...payload }));
    setEditingPlantRate((prev) => (prev ? { ...prev, ...payload } : prev));
    setShowMarginSetupModal(false);
    await refreshPlantRateTable(editingPlantRate?.id);
  };
  const saveMinimumMonthlyCharge = async () => {
    if (!editingPlantRate?.id) return;
    const visibleFields = minimumMonthlyChargeFields.filter((field) => !field.condition || field.condition(minimumMonthlyChargeForm));
    const validationErrors = validateForm(visibleFields, minimumMonthlyChargeForm);
    setMinimumMonthlyChargeErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;
    const payload = {
      minimumBasis: minimumMonthlyChargeForm.minimumBasis || 'hours',
      minimumHours: Number(minimumMonthlyChargeForm.minimumHours || 0),
      minimumCharge: Number(minimumMonthlyChargeForm.minimumCharge || 0),
      updatedBy: userProfile?.id,
      updatedAt: new Date().toISOString(),
    };
    await updatePlantRates(editingPlantRate.id, payload);
    setPlantRateForm((prev) => ({ ...prev, ...payload }));
    setEditingPlantRate((prev) => (prev ? { ...prev, ...payload } : prev));
    setShowMinimumMonthlyChargeModal(false);
  };


  useEffect(() => {
    const run = async () => {
      const method = plantRateForm.depreciationBasis || 'none';
      if (method === 'none') {
        setPlantRateForm((prev) => ({ ...prev, residualValue: 0, depreciationAmount: 0, depreciationRatePerUsage: 0 }));
        return;
      }
      const payload = {
        purchasePrice: Number(plantRateForm.purchasePrice || 0),
        startUsage: Number(plantRateForm.startUsage || 0),
        endUsage: Number(plantRateForm.endUsage || 0),
        depreciationMethod: method,
        depreciationRate: Number(plantRateForm.depreciationRate || 0),
        depreciationEndHours: Number((plantRateForm.depreciationBasis === 'declining_balance_hours' && Number(plantRateForm.depreciationDbProfile || 0) === 0) ? (plantRateForm.endUsage || 0) : (plantRateForm.depreciationEndHours || 0)),
        residualValue: Number(plantRateForm.residualValue || 0),
        depreciationDbUsage: Number(plantRateForm.depreciationDbUsage || 0),
        depreciationDbProfile: Number(plantRateForm.depreciationDbProfile || 0),
        depreciationMonths: Number(plantRateForm.depreciationMonths || 0),
      };

      const result = await calculatePlantRateDepreciation(payload);
      setPlantRateForm((prev) => ({ ...prev, depreciationAmount: result.depreciationAmount, depreciationRatePerUsage: result.depreciationRatePerUsage, residualValue: method === 'straight_line' ? prev.residualValue : result.residualValue }));
    };
    run();
  }, [plantRateForm.depreciationBasis, plantRateForm.depreciationRate, plantRateForm.depreciationEndHours, plantRateForm.depreciationDbUsage, plantRateForm.deprecationDbProfile, plantRateForm.depreciationMonths, plantRateForm.purchasePrice, plantRateForm.startUsage, plantRateForm.endUsage, plantRateForm.residualValue]);

  useEffect(() => {
    if (!showInterestModal) return;
    const rounded = Number(Number(plantRateForm.interestRate || 0).toFixed(2));
    if (Number(plantRateForm.interestRate || 0) !== rounded) {
      setPlantRateForm((prev) => ({ ...prev, interestRate: rounded }));
    }
  }, [showInterestModal]);

  useEffect(() => {
    const visible = interestFields.filter((f) => !f.condition || f.condition(plantRateForm));

    if (Object.keys(validateForm(visible, plantRateForm)).length) return;

    const method = plantRateForm.interestCalculationMethod || 'average_investment';

    calculatePlantRateInterest({
      purchasePrice: Number(plantRateForm.purchasePrice || 0),
      interestRate: Number(plantRateForm.interestRate || 0),
      endUsage: Number(plantRateForm.endUsage || 0),
      startUsage: Number(plantRateForm.startUsage || 0),
      utilisation: Number(plantRateForm.utilisation || 0),
      utilisation_percent: Number(plantRateForm.utilisationPercent ?? plantRateForm.utilisation_percent ?? 100),
      residualValue: Number(plantRateForm.residualValue || 0),
      numberOfMonths: Number(plantRateForm.numberOfMonths || 0),
      balloonPayment: Number(plantRateForm.balloonPayment || 0),
      interestCalculationMethod: method, waccDebtPercent: Number(plantRateForm.waccDebtPercent || 0),
      waccDebtRate: Number(plantRateForm.waccDebtRate || 0),
      waccEquityPercent: Number(plantRateForm.waccEquityPercent || 0),
      waccEquityRate: Number(plantRateForm.waccEquityRate || 0),
    }).then((result) =>
      setPlantRateForm((prev) => ({
        ...prev,
        interestAnnualAmount: result.interestAnnualAmount,
        interestMonthlyAmount: result.interestMonthlyAmount,
        interestHourlyAmount: result.interestHourlyAmount,
        interestAverageInvestment: result.interestAverageInvestment,
        effectiveInterestRate: result.effectiveInterestRate
      }))).catch(() => { });
  }, [interestFields, plantRateForm.interestCalculationMethod, plantRateForm.interestRate, plantRateForm.endUsage, plantRateForm.startUsage, plantRateForm.utilisation, plantRateForm.utilisationPercent, plantRateForm.utilisation_percent, plantRateForm.residualValue, plantRateForm.numberOfMonths, plantRateForm.balloonPayment, plantRateForm.waccDebtPercent, plantRateForm.waccDebtRate, plantRateForm.waccEquityPercent, plantRateForm.waccEquityRate, plantRateForm.purchasePrice]);



  useEffect(() => {
    const method = plantRateForm.insuranceCalculationMethod || 'percent_purchase_price';
    if (method === 'fixed') return;
    calculatePlantRateInsurance({
      purchasePrice: Number(plantRateForm.purchasePrice || 0),
      insuranceRate: Number(plantRateForm.insuranceRate || 0),
      insuranceCalculationMethod: method,
    }).then((result) => setPlantRateForm((prev) => ({ ...prev, insuranceMonthlyAmount: result.insuranceMonthlyAmount }))).catch(() => { });
  }, [plantRateForm.insuranceCalculationMethod, plantRateForm.purchasePrice, plantRateForm.insuranceRate]);

  return <>
    {headerSearchContainer && createPortal(
      <input type="text" className="form-control form-control-sm" style={{ width: '200px' }} placeholder="Search projects..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />, headerSearchContainer)}
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div className="d-flex gap-2">
          {renderFilterControls()}
        </div>
        <button className="btn btn-primary text-sm px-16 py-12 radius-4 d-flex align-items-center gap-2" onClick={openCreateProjectModal}>
          <Icon style={{ position: "relative", top: "-1px" }} icon="ic:baseline-plus" /> Add Project
        </button>
      </div>
      <div className="card-body p-16" style={{ height: "calc(100vh - 210px)" }}>
        <div className="row g-3">
          <div className="col-md-4">
            <h6 className="text-xl mt-24 mb-36 ms-8">Projects</h6>
            <table className="table table-hover mb-0 panel-table">
              <tbody>
                {projects.length === 0 && (
                  <tr className='border-bottom'>
                    <td className="text-center py-4 rounded-0">No Projects</td>
                  </tr>
                )}
                {groupedProjects.map((group) => (
                  <React.Fragment key={group.key}>
                    <tr className="table-light">
                      <td className="bg-neutral-50">
                        <button
                          type="button"
                          className="btn btn-link d-flex align-items-center gap-2 px-8 py-8 text-decoration-none text-black"
                          onClick={() =>
                            setOpenProjectGroupKeys((prev) => ({
                              ...prev, [group.key]: !prev[group.key]
                            }))
                          }
                        >
                          <Icon
                            icon={openProjectGroupKeys[group.key] ? 'mdi:chevron-down' : 'mdi:chevron-right'}
                          />
                          <span className="fw-semibold">{group.label}</span>
                          <span className="text-muted text-sm">({group.items.length})</span>
                        </button>
                      </td>
                    </tr>
                    {openProjectGroupKeys[group.key] && group.items.map((project) => (
                      <tr
                        key={project.id}
                        onClick={() => { setSelectedProject(project); fetchPlantRates(project.id); }}
                        className={selectedProject?.id === project.id ? 'notification-selected' : ''}
                        role="button"
                      >
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="d-flex align-items-center justify-content-center flex-shrink-0 me-12 py-12" style={{ width: '60px' }}>
                              <div className="alert alert-primary p-10 mb-0">
                                <Icon icon="mdi:landscape-outline" className="text-primary" style={{ fontSize: '24px' }} />
                              </div>
                            </div>
                            <div className="flex-grow-1 px-">
                              <div className="text-sm">#{project.projectNumber || project.id}</div>
                              <div className="fw-bold">{project.name || '-'}</div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="col-md-8">
            <div className="d-flex justify-content-between pe-8 mb-16">
              <h6 className="text-xl py-16">Plant Rates {selectedProject ? `- ${selectedProject.name}` : ''}</h6>
              <button
                className="btn btn-outline-primary text-sm px-16 py-12 radius-4 d-flex align-items-center gap-2 my-8"
                disabled={!selectedProject}
                onClick={() => {
                  setEditingPlantRate(null);
                  setEndMode('usage');
                  setPlantRateForm({
                    interestRate: Number(selectedProject?.interestRate || 0),
                    insuranceRate: Number(selectedProject?.insuranceRate || 0),
                    startUsage: 0,
                    utilisation: 1000,
                    endUsage: 10000,
                    depreciationRate: 15.00,
                    depreciationMonths: calculateDefaultDepreciationMonths(0, 10000),
                    depreciationEndHours: 10000,
                    measurementUnitsId: 1,
                    currencyId: 1,
                    depreciationBasis: 'none',
                    interestCalculationMethod: 'average_investment',
                    breakdownProfile: 'flat',
                    insuranceCalculationMethod: 'percent_purchase_price'
                  });
                  setActiveTab('general');
                  setShowPlantRateModal(true);
                }}>
                <Icon icon="ic:baseline-plus" />
                Add Plant Rate
              </button>
            </div>
            <table className="table bordered-table rounded-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Group</th>
                  <th>Measurement Unit</th>
                  <th>Currency</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlantRates.length === 0 && (
                  <tr className='border-bottom'>
                    <td className="text-center py-4 rounded-0">No Plant Rates Found</td>
                  </tr>
                )}
                {filteredPlantRates.map((r) => <tr key={r.id}>
                  <td>{r.name}</td><td>{r.group?.groupName}</td>
                  <td>{r.measurementUnits?.unitName}</td>
                  <td>{r.currency ? `${r.currency.name} (${r.currency.symbol || ''})` : ''}</td>
                  <td>
                    {canEdit && (
                      <button
                        className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                        onClick={() => {
                          setEditingPlantRate(r);
                          setEditInputDraft({});
                          setDirtyPlantRateFields({});
                          setPlantRateForm({ ...r, insuranceCalculationMethod: r.insuranceCalculationMethod || 'percent_purchase_price', fuelHourlyCost: Number(selectedProject?.fuelPrice || 0) * Number(r.fuelUsage || 0) });
                          setEndMode(r.endDate ? 'date' : 'usage');
                          setErrors({});
                          setShowEditPlantRateModal(true);
                        }}
                      >
                        <Icon icon="ic:baseline-edit" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center ms-6"
                        onClick={async () => { if (window.confirm('Delete plant rate?')) { await deletePlantRates(r.id); await fetchPlantRates(selectedProject?.id); } }}
                      >
                        <Icon icon="ic:baseline-delete-outline" />
                      </button>
                    )}
                  </td>
                </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    {showProjectModal &&
      <div className="modal show d-block" style={{ zIndex: 10000 }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Project Settings</h5>
              <button className="btn-close" onClick={() => setShowProjectModal(false)} /></div>
            <div className="modal-body">
              <div className="row g-3">
                {projectSettingsFields.map(renderProjectSettingsField)}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowProjectModal(false)}>Close</button>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  const validationErrors = validateForm(projectSettingsFields, projectForm);
                  setProjectSettingsErrors(validationErrors);
                  if (Object.keys(validationErrors).length) return;
                  // Remove extra fields before sending to API
                  delete projectForm.tblAssets;
                  if (selectedProject?.id) {
                    await updateProjects(selectedProject.id, projectForm);
                  } else {
                    await createProjects(projectForm);
                  }
                  await fetchProjectsData();
                  if (selectedProject?.id) {
                    const refreshedProject = (await getProjects())?.projects?.find((p) => Number(p.id) === Number(selectedProject.id));
                    if (refreshedProject) setSelectedProject(refreshedProject);
                  }
                  setShowProjectModal(false);
                }}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    }

    {
      showPlantRateModal &&
      <div className='modal show d-block'>
        <div className='modal-dialog modal-xl'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>{editingPlantRate ? 'Edit Plant Rate' : 'Add Plant Rate'}</h5>
              <button className='btn-close' onClick={() => setShowPlantRateModal(false)} />
            </div>
            <div className='modal-body'>

              <div className='row g-3'>{formFields.general.map(renderGeneralField)}</div>
            </div>
            <div className='modal-footer'>
              <button
                className='btn btn-secondary'
                onClick={() => setShowPlantRateModal(false)}>
                Close
              </button>
              <button
                className='btn btn-primary'
                onClick={savePlantRate}>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    }

    <PlantHireRateForm
      show={showEditPlantRateModal}
      editingPlantRate={editingPlantRate}
      openPlantRateInputGroupKeys={openPlantRateInputGroupKeys}
      setOpenPlantRateInputGroupKeys={setOpenPlantRateInputGroupKeys}
      plantRateInputGroups={plantRateInputGroups}
      renderEditField={renderEditField}
      plantRateForm={plantRateForm}
      plantRateTableRows={plantRateTableRows}
      plantRateCategories={plantRateCategories}
      planningOccurrencesResult={planningOccurrencesResult}
      onOpenPlantRateCategories={() => setShowPlantRateCategoriesModal(true)}
      onOpenProjectSettings={openProjectSettingsModal}
      onClose={() => setShowEditPlantRateModal(false)}
      onSave={savePlantRate}
      projectSettings={selectedProject}
    />
    {showMaintenanceTasksModal && (
      <div className='modal show d-block' style={{ zIndex: 1080 }}>
        <div className='modal-dialog modal-xxl'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Maintenance Task Setup</h5>
              <button className='btn-close' onClick={async () => { setShowMaintenanceTasksModal(false); await refreshPlantRateTable(editingPlantRate?.id); }} />
            </div>
            <div className='modal-body'>
              <div className='row g-3 align-items-end'>
                {maintenanceTaskFields.map((field) => (
                  <React.Fragment key={field.field}>{renderMaintenanceField(field)}</React.Fragment>
                ))}
              </div>
              <div className='d-flex justify-content-between align-items-center mt-16 mb-12'>
                <input className='form-control' style={{ maxWidth: 320 }} placeholder='Search Tasks' value={maintenanceTaskSearch} onChange={(e) => setMaintenanceTaskSearch(e.target.value)} />
                <div className='btn-group'>
                  <button className='btn btn-primary' onClick={async () => {
                    console.log('editingPlantRate', editingPlantRate);
                    if (!editingPlantRate?.id) return;
                    const created = await createTasks({
                      taskName: 'New Task',
                      taskProfile: 'project',
                      plantRateId: editingPlantRate.id,
                      assetId: null,
                      taskTypeId: 1,
                      defaultCostCentre: null,
                    });
                    const createdTaskId = created?.id || null;
                    console.log('createdTaskId', createdTaskId);
                    setNewMaintenanceTaskId(createdTaskId);
                    setEditingMaintenanceTaskId(createdTaskId);
                  }}>+ Add Task</button>
                  <button type='button' className='btn btn-primary dropdown-toggle dropdown-toggle-split' data-bs-toggle='dropdown' aria-expanded='false'>
                    <span className='visually-hidden'>Toggle Dropdown</span>
                  </button>
                  <ul className='dropdown-menu dropdown-menu-end'>
                  <li className='my-6'>
                    <button
                      className="dropdown-item py-8">
                      <Icon
                        icon="mage:clipboard-2"
                        style={{ position: "relative", top: "-2px" }}
                        className="icon text-xl line-height-1 me-8"
                      />
                      Import from Task Template
                      <span className="visually-hidden">Toggle Dropdown</span>
                    </button>
                  </li>
                  <li className='my-6'>
                    <button
                      className="dropdown-item py-8"
  
                    >
                      <Icon
                        icon="mage:delivery-truck"
                        className="icon text-xl line-height-1 me-8"
                        style={{ position: "relative", top: "-2px" }}
                      />
                      Copy from Existing Asset
                    </button>
                  </li>
                  <li className='my-6'>
                    <button
                      className="dropdown-item  py-8"

                    >
                      <Icon
                        icon="mage:upload"
                        className="icon text-xl line-height-1 me-8"
                        style={{ position: "relative", top: "-2px" }}
                      />
                      Import From File
                    </button>
                  </li>
                  </ul>
                </div>
              </div>
              <table className='table bordered-table'>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Modifier</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Freq.</th>
                    <th>Units</th>
                    <th className="text-center">Parts</th>
                    <th className="text-center">Labour</th>
                    <th className="text-center">Lab. Hrs</th>
                    <th className="text-center">Total</th>
                    <th className="text-center">Cost / Hour</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const usageRange = Number(editingPlantRate?.endUsage || 0) - Number(editingPlantRate?.startUsage || 0);
                    const totalRatePerUsage = filteredMaintenanceTasks.reduce((sum, task) => {
                      const partsCost = Number(task.partsPrice || 0) + Number(task.miscPrice || 0);
                      const labourCost = Number(task.labourHours || 0) * Number(selectedProject?.technicianRate || 0);
                      const rowRatePerUsage = task.frequency > 0 ? (partsCost + labourCost) / task.frequency : 0;
                      return sum + rowRatePerUsage;
                    }, 0);

                    return (
                      <>
                        {filteredMaintenanceTasks.map((task) => {
                          const partsCost = Number(task.partsPrice || 0) + Number(task.miscPrice || 0);
                          const labourCost = Number(task.labourHours || 0) * Number(selectedProject?.technicianRate || 0);
                          const rowRatePerUsage = task.frequency > 0 ? (partsCost + labourCost) / task.frequency : null;

                          return (
                            <tr key={task.id}>
                              <td style={{ width: '100px' }}>{task.code?.code || '-'}</td>
                              <td style={{ width: '100px' }}>{task.modifier?.code || '-'}</td>
                              <td>{task.name || '-'}</td>
                              <td style={{ width: '100px' }}>{task.taskType?.taskTypeCode || '-'}</td>
                              <td style={{ width: '100px' }}>{task.frequency || '-'}</td>
                              <td style={{ width: '100px' }}>{task.plantMeterId === 1 ? 'Hours' : 'Days'}</td>
                              <td className="text-end" style={{ width: '100px' }}>{partsCost}</td>
                              <td className="text-end" style={{ width: '100px' }}>{labourCost}</td>
                              <td className="text-end" style={{ width: '100px' }}>{task.labourHours || '-'}</td>
                              <td className="text-end" style={{ width: '100px' }}>{partsCost + labourCost}</td>
                              <td className="text-end" style={{ width: '100px' }}>{parseInt(rowRatePerUsage * 100, 10) / 100 ?? '-'}</td>
                              <td className="text-center" style={{ width: '120px' }}>
                                <button className='edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center' onClick={() => { setNewMaintenanceTaskId(null); setEditingMaintenanceTaskId(task.id); }}><Icon icon="ic:baseline-edit" /></button>
                                <button className='delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center ms-6' onClick={async () => { await deleteTasks(task.id); setMaintenanceTasks((prev) => prev.filter((row) => row.id !== task.id)); }}><Icon icon="ic:baseline-delete-outline" /></button>
                              </td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td colSpan={10} className='text-end fw-semibold'>Total</td>
                          <td className='fw-semibold text-end'>{usageRange > 0 ? parseInt(totalRatePerUsage * 100, 10) / 100 : '-'}</td>
                          <td />
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
            <div className='modal-footer'>
              <button className='btn btn-secondary' onClick={async () => {
                setShowMaintenanceTasksModal(false);
                await refreshPlantRateTable(editingPlantRate?.id);
              }}>Close</button>
              <button className='btn btn-primary' onClick={async () => {
                const validationErrors = validateForm(maintenanceTaskFields, plantRateForm);
                setMaintenanceTaskErrors(validationErrors);
                if (Object.keys(validationErrors).length) return;
                await updatePlantRates(editingPlantRate.id, { overrideMaintenance: Number(plantRateForm.overrideMaintenance) === 1 ? 1 : 0, maintenanceValue: Number(plantRateForm.maintenanceValue || 0) });
                setShowMaintenanceTasksModal(false);
                await refreshPlantRateTable(editingPlantRate?.id);
              }}>Save</button>
            </div>
          </div>
        </div>
      </div>
    )}
    {editingMaintenanceTaskId !== null && (
      <TaskEditModal
        taskId={editingMaintenanceTaskId || 0}
        show={editingMaintenanceTaskId !== null}
        showCostFields
        onClose={async () => {
          if (newMaintenanceTaskId && Number(newMaintenanceTaskId) === Number(editingMaintenanceTaskId)) {
            await deleteTasks(newMaintenanceTaskId);
            setMaintenanceTasks((prev) => prev.filter((row) => Number(row.id) !== Number(newMaintenanceTaskId)));
          }
          setNewMaintenanceTaskId(null);
          setEditingMaintenanceTaskId(null);
          await refreshPlantRateTable(editingPlantRate?.id);
        }}
        onSaved={async () => {
          const rows = await getTasksByPlantRate(editingPlantRate?.id);
          setMaintenanceTasks(rows || []);
          setNewMaintenanceTaskId(null);
          setEditingMaintenanceTaskId(null);
          await refreshPlantRateTable(editingPlantRate?.id);
        }}
      />
    )}

    {showPlantRateCategoriesModal && (
      <div className='modal show d-block'>
        <div className='modal-dialog modal-lg'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Plant Rate Categories</h5>
              <button className='btn-close' onClick={() => setShowPlantRateCategoriesModal(false)} />
            </div>
            <div className='modal-body'>
              <table className='table bordered-table'>
                <thead><tr><th>Name</th><th>Default Expense</th><th>Include</th></tr></thead>
                <tbody>
                  {plantRateCategories.map((cat) => (
                    <tr key={cat.id}>
                      <td>{cat.name}</td>
                      <td>
                        <select className='form-select' value={cat.defaultExpense ?? ''} onChange={async (e) => {
                          const defaultExpense = e.target.value === '' ? null : Number(e.target.value);
                          await updatePlantRateCategories(cat.id, { defaultExpense });
                          setPlantRateCategories((prev) => prev.map((r) => r.id === cat.id ? { ...r, defaultExpense } : r));
                        }}>
                          <option value=''>Select...</option>
                          {expenseCodes.map((ex) => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
                        </select>
                      </td>
                      <td>
                        <div className='form-switch switch-primary'>
                          <input className='form-check-input' type='checkbox' role='switch' checked={Number(cat.include) === 1} onChange={async (e) => {
                            const include = e.target.checked ? 1 : 0;
                            await updatePlantRateCategories(cat.id, { include });
                            setPlantRateCategories((prev) => prev.map((r) => r.id === cat.id ? { ...r, include } : r));
                          }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className='modal-footer'>
              <button className='btn btn-secondary' onClick={() => setShowPlantRateCategoriesModal(false)}>Close</button>
            </div>
          </div>
        </div>
      </div>
    )}

    {showPurchasePriceModal && (
      <div className='modal show d-block'>
        <div className='modal-dialog modal-lg'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Purchase Price</h5>
              <button className='btn-close' onClick={() => setShowPurchasePriceModal(false)} />
            </div>
            <div className='modal-body'>
              <div className='mb-3'>
                <label className='form-label'>Purchase Price</label>
                <input
                  className='form-control'
                  type='number'
                  step='1'
                  disabled={Number(plantRateForm.overridePurchase) !== 1}
                  value={plantRateForm.purchasePrice ?? ''}
                  onChange={(e) => setPlantRateForm((prev) => ({ ...prev, purchasePrice: e.target.value }))}
                />
              </div>
              <div className='form-switch switch-primary my-24'>
                <input
                  className='form-check-input me-8'
                  type='checkbox'
                  role="switch"
                  checked={Number(plantRateForm.overridePurchase) === 1}
                  onChange={(e) => setPlantRateForm((prev) => ({ ...prev, overridePurchase: e.target.checked ? 1 : 0 }))}
                />
                <label className='form-check-label'>Override Purchase Calculation</label>
              </div>
              <table className='table bordered-table'>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th style={{ width: "240px" }}>Currency</th>
                    <th style={{ width: "140px" }}>Currency Rate</th>
                    <th style={{ width: "200px" }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {purchasePriceItems.map((item) => {
                    const disableRate = Number(item.currencyId) === 1;
                    const currencyRate = disableRate ? 1 : Number(item.currencyRate || 1);
                    return (
                      <tr key={item.id}>
                        <td>{item.name || '-'}</td>
                        <td>
                          <select
                            className='form-select'
                            disabled={Number(plantRateForm.overridePurchase) === 1}
                            value={item.currencyId ?? 1}
                            onChange={(e) => {
                              const selectedCurrencyId = Number(e.target.value);
                              setPurchasePriceItems((prev) =>
                                prev.map((p) =>
                                (p.id === item.id ? {
                                  ...p,
                                  currencyId: selectedCurrencyId,
                                  currencyRate: selectedCurrencyId === 1 ? 1 :
                                    (p.currencyRate || 1)
                                } : p))
                              );
                            }}
                          >
                            {currencyOptions.map((option) =>
                              <option
                                key={option.value}
                                value={option.value}>
                                {option.label}
                              </option>)}
                          </select>
                        </td>
                        <td>
                          <input
                            className='form-control'
                            type='number'
                            min='0'
                            step='0.001'
                            disabled={Number(plantRateForm.overridePurchase) === 1 || disableRate}
                            value={currencyRate}
                            onChange={(e) => {
                              const val = e.target.value;
                              setPurchasePriceItems((prev) => prev.map((p) => (p.id === item.id ? { ...p, currencyRate: val } : p)));
                            }}
                          />
                        </td>
                        <td>
                          <input
                            className={`form-control text-end ${purchasePriceItemErrors[item.id] ? 'is-invalid' : ''}`}
                            type='number'
                            min='0'
                            step='1'
                            disabled={Number(plantRateForm.overridePurchase) === 1}
                            value={item.cost != null ? parseInt(item.cost, 10) : ''}
                            onChange={(e) => {
                              const val = e.target.value === '' ? '' : parseInt(e.target.value, 10);

                              setPurchasePriceItems((prev) =>
                                prev.map((p) =>
                                  p.id === item.id
                                    ? { ...p, cost: val }
                                    : p
                                )
                              );
                              setPurchasePriceItemErrors((prev) => ({
                                ...prev,
                                [item.id]: undefined,
                              }));
                            }}
                          />
                          {purchasePriceItemErrors[item.id] && (
                            <div className='invalid-feedback'>
                              {purchasePriceItemErrors[item.id]}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td colSpan={3} className='fw-semibold text-start text-md'>
                      Total
                    </td>

                    <td>
                      <input
                        type='text'
                        className='form-control text-end fw-semibold'
                        value={formatWholeNumber(purchasePriceFromItems)}
                        disabled
                        readOnly
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className='modal-footer'>
              <button className='btn btn-secondary' onClick={() => setShowPurchasePriceModal(false)}>Close</button>
              <button className='btn btn-primary' onClick={savePurchasePriceModal}>Save</button>
            </div>
          </div>
        </div>
      </div>
    )}

    {showAddGroupModal && <div className='modal show d-block'>
      <div className='modal-dialog'>
        <div className='modal-content'>
          <div className='modal-header'><h5 className='modal-title'>Add Group</h5><button className='btn-close' onClick={() => setShowAddGroupModal(false)} /></div>
          <div className='modal-body'><div className='row g-3'>{addGroupFields.map(renderAddGroupField)}</div></div>
          <div className='modal-footer'><button className='btn btn-secondary' onClick={() => setShowAddGroupModal(false)}>Close</button><button className='btn btn-primary' onClick={handleSaveGroup}>Save Group</button></div>
        </div>
      </div>
    </div>}

    {showDepreciationModal && <div className='modal show d-block'><div className='modal-dialog modal-lg'><div className='modal-content'><div className='modal-header'><h5 className='modal-title'>Depreciation Method</h5><button className='btn-close' onClick={() => setShowDepreciationModal(false)} /></div><div className='modal-body'><div className='row g-3'>{depreciationFields.filter((f) => !f.condition || f.condition(plantRateForm)).map(renderDepreciationField)}<div className='col-12'><label className='form-label'>Depreciation Amount</label><input className='form-control' disabled value={plantRateForm.depreciationAmount ?? 0} /></div><div className='col-12'><label className='form-label'>Depreciation Rate Per Usage</label><input className='form-control' disabled value={plantRateForm.depreciationRatePerUsage ?? 0} /></div></div></div><div className='modal-footer'><button className='btn btn-secondary' onClick={() => setShowDepreciationModal(false)}>Close</button><button className='btn btn-primary' onClick={saveDepreciationMethod}>Save</button></div></div></div></div>}

    {showInterestModal &&
      <div className='modal show d-block'>
        <div className='modal-dialog modal-lg'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Interest Calculation</h5>
              <button className='btn-close' onClick={() => setShowInterestModal(false)} />
            </div>
            <div className='modal-body'>
              <div className='row g-3'>{interestFields.filter((f) => !f.condition || f.condition(plantRateForm)).map(renderInterestField)}
                <div className='col-6'>
                  <label className='form-label'>Interest Annual Amount</label>
                  <input className='form-control' disabled value={Number(plantRateForm.interestAnnualAmount || 0).toFixed(2)} />
                </div>
                <div className='col-6'>
                  <label className='form-label'>Interest Monthly Amount</label>
                  <input className='form-control' disabled value={Number(plantRateForm.interestMonthlyAmount || 0).toFixed(2)} />
                </div>
                <div className='col-6'>
                  <label className='form-label'>Interest Hourly Amount</label>
                  <input className='form-control' disabled value={Number(plantRateForm.interestHourlyAmount || 0).toFixed(2)} />
                </div>
                <div className='col-6'>
                  <label className='form-label'>Interest Average Investment</label>
                  <input className='form-control' disabled value={Number(plantRateForm.interestAverageInvestment || 0).toFixed(2)} />
                </div>
              </div>
            </div>
            <div className='modal-footer'>
              <button className='btn btn-secondary' onClick={() => setShowInterestModal(false)}>Close</button>
              <button className='btn btn-primary' onClick={saveInterestMethod}>Save</button>
            </div>
          </div>
        </div>
      </div>}

    {showTyreSetupModal && <div className='modal show d-block'>
      <div className='modal-dialog modal-lg'>
        <div className='modal-content'>
          <div className='modal-header'><h5 className='modal-title'>Tyre and Rim Setup</h5><button className='btn-close' onClick={() => setShowTyreSetupModal(false)} /></div>
          <div className='modal-body'>
            <div className='row g-3'>
              {tyreSetupFields.filter((f) => !['rimLife', 'rimCost', 'notes'].includes(f.field)).map(renderTyreSetupField)}
              <div className='col-12'>
                <label className='form-label'>Include Rims</label>
                <div className='form-switch switch-primary'>
                  <input className='form-check-input ' type='checkbox' role="switch" checked={Number(tyreSetupForm.includeRims) === 1} onChange={(e) => setTyreSetupForm((prev) => ({ ...prev, includeRims: e.target.checked ? 1 : 0 }))} />
                </div>
              </div>
              {Number(tyreSetupForm.includeRims) === 1 && tyreSetupFields.filter((f) => ['rimLife', 'rimCost'].includes(f.field)).map(renderTyreSetupField)}
              {tyreSetupForm.tyreTypeImage && <div className='col-12'><img src={tyreSetupForm.tyreTypeImage} alt='Tyre type' style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} /></div>}
              {tyreSetupFields.filter((f) => f.field === 'notes').map(renderTyreSetupField)}
            </div>
          </div>
          <div className='modal-footer'><button className='btn btn-secondary' onClick={() => setShowTyreSetupModal(false)}>Close</button><button className='btn btn-primary' onClick={saveTyreSetup}>Save</button></div>
        </div>
      </div>
    </div>}

    {showFuelUsageModal && <div className='modal show d-block'>
      <div className='modal-dialog modal-lg'>
        <div className='modal-content'>
          <div className='modal-header'><h5 className='modal-title'>Fuel Usage Setup</h5><button className='btn-close' onClick={() => setShowFuelUsageModal(false)} /></div>
          <div className='modal-body'>
            <div className='row g-3'>
              {fuelUsageFields.map(renderFuelUsageField)}
            </div>
            <hr className="mt-36" />
            {fuelTemplateProfiles.length > 0 &&
              <div className='col-6 my-24'>
                <label className='form-label'>Optionally select a fuel consumption profile to apply</label>
                <select className='form-select' defaultValue='' onChange={(e) => applyFuelTemplateProfile(e.target.value)}>
                  <option value=''>Select...</option>
                  {fuelTemplateProfiles.map((row) => <option key={row.id} value={row.id}>{`${row.name || ''} - ${row.value ?? 0}`}</option>)}
                </select>
              </div>}
          </div>
          <div className='modal-footer'>
            <button className='btn btn-secondary' onClick={() => setShowFuelUsageModal(false)}>Close</button>
            <button className='btn btn-primary' onClick={saveFuelUsageSetup}>Save</button></div>
        </div>
      </div>
    </div>}

    {showOilFluidsModal && <div className='modal show d-block'>
      <div className='modal-dialog modal-xl'>
        <div className='modal-content'><
          div className='modal-header'>
          <h5 className='modal-title'>Oil & Fluids Setup</h5>
          <button className='btn-close' onClick={() => setShowOilFluidsModal(false)} />

        </div>
          <div className='modal-body'>
            <div className='d-flex justify-content-end mb-3'>
              <button
                className='btn btn-primary'
                onClick={() => {
                  setEditingFluid(null);
                  setFluidForm({
                    life: 0,
                    cost: 0,
                    currencyId: 1,
                    quantity: 0,
                    topUpPercent: 0
                  });
                  setShowFluidEditModal(true);
                }}
              >
                <Icon icon='ic:baseline-plus' className='me-2' style={{ position: 'relative', top: '-3px' }} />
                Add Fluid
              </button>
            </div>

            <table className='table bordered-table mb-36'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Compartment</th>
                  <th>Life</th>
                  <th>Cost</th>
                  <th>Currency</th>
                  <th>Quantity</th>
                  <th>Top Up %</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plantRateFluids.map((r) => <tr key={r.id}>
                  <td>{`${r.fluidItem?.manufacturer.manufacturer || ''} ${r.fluidItem?.name || ''} ${r.fluidItem?.fluidType || ''}`}</td>
                  <td>{r.compartment}</td>
                  <td>{r.life}</td>
                  <td>{r.cost}</td>
                  <td>{r.currency?.symbol || r.currencyId}</td>
                  <td>{r.quantity}</td>
                  <td>{r.topUpPercent}</td>

                  <td>
                    <div className='d-flex gap-2'>
                      <button className='w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center' onClick={() => { setEditingFluid(r); setFluidForm({ ...r }); setShowFluidEditModal(true); }}>
                        <Icon icon='ic:baseline-edit' className='icon text-xl line-height-1' />
                      </button>
                      <button className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center' onClick={async () => { await deletePlantRateFluids(r.id); setPlantRateFluids(await getPlantRateFluidsByPlantRateId(editingPlantRate.id)); }}>
                        <Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' />
                      </button>
                    </div>
                  </td>
                </tr>)}
              </tbody>
            </table>

            <hr className='pb-24' />

            <div className='form-label mb-16'>Optionally add from template</div>

            {!!editingPlantRate?.groupId &&
              <button
                className='btn btn-outline-primary mt-2'
                onClick={async () => {
                  await replacePlantRateFluidsFromTemplate({
                    plantRateId: editingPlantRate.id,
                    groupId: editingPlantRate.groupId
                  });
                  const updatedFluids = await getPlantRateFluidsByPlantRateId(editingPlantRate.id);
                  await Promise.all((updatedFluids || []).map((row) =>
                    updatePlantRateFluids(row.id, { ...row, cost: 0 })));
                  setPlantRateFluids(await
                    getPlantRateFluidsByPlantRateId(editingPlantRate.id));
                }}
                disabled={fluidTemplatesForGroup.length === 0}>
                Apply Template
              </button>}

            {fluidTemplatesForGroup.length > 0 && <>
              <table className='table bordered-table mt-24 mb-0'>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Compartment</th>
                    <th>Life</th>
                    <th>Currency</th>
                    <th>Quantity</th>
                    <th>Top Up %</th>
                  </tr>
                </thead>
                <tbody>
                  {fluidTemplatesForGroup.map((r) => <tr key={`template-${r.id}`}>
                    <td style={{ backgroundColor: "var(--neutral-50)" }}>{`${r.fluidItem?.manufacturer.manufacturer || ''} ${r.fluidItem?.name || ''} ${r.fluidItem?.fluidType || ''}`}</td>
                    <td style={{ backgroundColor: "var(--neutral-50)" }}>{r.compartment}</td>
                    <td style={{ backgroundColor: "var(--neutral-50)" }}>{r.life}</td>
                    <td style={{ backgroundColor: "var(--neutral-50)" }}>{r.currency?.symbol || r.currencyId}</td>
                    <td style={{ backgroundColor: "var(--neutral-50)" }}>{r.quantity}</td>
                    <td style={{ backgroundColor: "var(--neutral-50)" }}>{r.topUpPercent}</td>
                  </tr>)}
                </tbody>
              </table>
            </>}
          </div><div className='modal-footer'><button className='btn btn-secondary' onClick={() => setShowOilFluidsModal(false)}>Close</button></div></div></div></div>}

    {showFluidEditModal && <div className='modal show d-block'><div className='modal-dialog modal-lg'><div className='modal-content'><div className='modal-header'><h5 className='modal-title'>{editingFluid ? 'Edit Fluid' : 'Add Fluid'}</h5><button className='btn-close' onClick={() => setShowFluidEditModal(false)} /></div><div className='modal-body'><div className='row g-3'>{fluidFields.map(renderFluidField)}</div></div><div className='modal-footer'><button className='btn btn-secondary' onClick={() => setShowFluidEditModal(false)}>Close</button><button className='btn btn-primary' onClick={saveFluid}>Save</button></div></div></div></div>}

    {showGetSetupModal && <div className='modal show d-block'><div className='modal-dialog modal-xl'><div className='modal-content'><div className='modal-header'><h5 className='modal-title'>GET and Liners Setup</h5><button className='btn-close' onClick={() => setShowGetSetupModal(false)} /></div><div className='modal-body'>
      <button className='btn btn-outline-primary mb-3' onClick={() => { setEditingGet(null); setGetForm({ life: 0, cost: 0, currencyId: 1, quantity: 0 }); setShowGetEditModal(true); }}><Icon icon='ic:baseline-plus' /> Add GET profile</button>
      <table className='table bordered-table'><thead><tr><th>Part Number</th><th>Name</th><th>Life</th><th>Cost</th><th>Currency</th><th>Quantity</th><th>Actions</th></tr></thead><tbody>{plantRateGets.map((r) => <tr key={r.id}><td>{r.partNumber}</td><td>{r.name}</td><td>{r.life}</td><td>{r.cost}</td><td>{r.currency?.symbol || r.currencyId}</td><td>{r.quantity}</td><td><div className='d-flex gap-2'><button className='w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center' onClick={() => { setEditingGet(r); setGetForm({ ...r }); setShowGetEditModal(true); }}><Icon icon='ic:baseline-edit' className='icon text-xl line-height-1' /></button><button className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center' onClick={async () => { await deletePlantRateGetList(r.id); setPlantRateGets(await getPlantRateGetByPlantRateId(editingPlantRate.id)); }}><Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' /></button></div></td></tr>)}</tbody></table>
      <hr />
      <div>Optionally add from template.</div>
      {!!editingPlantRate?.groupId && <button className='btn btn-outline-primary mt-2' onClick={async () => { await replacePlantRateGetFromTemplate({ plantRateId: editingPlantRate.id, groupId: editingPlantRate.groupId }); setPlantRateGets(await getPlantRateGetByPlantRateId(editingPlantRate.id)); }} disabled={!hasGetTemplatesForGroup}>Add</button>}
    </div><div className='modal-footer'><button className='btn btn-secondary' onClick={() => setShowGetSetupModal(false)}>Close</button></div></div></div></div>}

    {showGetEditModal && <div className='modal show d-block'><div className='modal-dialog modal-lg'><div className='modal-content'><div className='modal-header'><h5 className='modal-title'>{editingGet ? 'Edit GET profile' : 'Add GET profile'}</h5><button className='btn-close' onClick={() => setShowGetEditModal(false)} /></div><div className='modal-body'><div className='row g-3'>{getFields.map(renderGetField)}</div></div><div className='modal-footer'><button className='btn btn-secondary' onClick={() => setShowGetEditModal(false)}>Close</button><button className='btn btn-primary' onClick={saveGet}>Save</button></div></div></div></div>}

    {showOverheadSetupModal && (
      <div className='modal show d-block'>
        <div className='modal-dialog modal-md'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Overhead Setup</h5>
              <button className='btn-close' onClick={() => setShowOverheadSetupModal(false)} />
            </div>
            <div className='modal-body'>
              <div className='mb-12'>
                <label className='form-label fw-medium'>Overhead Percent</label>
                <input
                  type='number'
                  className='form-control'
                  value={overheadSetupForm.overheadPercent ?? 0}
                  onChange={(e) => setOverheadSetupForm((prev) => ({ ...prev, overheadPercent: e.target.value }))}
                />
              </div>
            </div>
            <div className='modal-footer'>
              <button className='btn btn-secondary' onClick={() => setShowOverheadSetupModal(false)}>Close</button>
              <button className='btn btn-primary' onClick={saveOverheadSetup}>Save</button>
            </div>
          </div>
        </div>
      </div>
    )}

    {showMarginSetupModal && (
      <div className='modal show d-block'>
        <div className='modal-dialog modal-md'>
          <div className='modal-content'>
            <div className='modal-header'><h5 className='modal-title'>Margin Setup</h5><button className='btn-close' onClick={() => setShowMarginSetupModal(false)} /></div>
            <div className='modal-body'>
              <div className='mb-3'>
                <label className='form-label'>Margin Method</label>
                <select className='form-select' value={marginSetupForm.marginMethod || 'percent'} onChange={(e) => setMarginSetupForm((prev) => ({ ...prev, marginMethod: e.target.value }))}>
                  {marginMethodOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {(marginSetupForm.marginMethod || 'percent') === 'percent' && <div className='mb-3'>
                <label className='form-label'>Margin Percent</label>
                <input className='form-control' type='number' value={marginSetupForm.marginPercent ?? 0} onChange={(e) => setMarginSetupForm((prev) => ({ ...prev, marginPercent: e.target.value }))} />
              </div>}
              {(marginSetupForm.marginMethod || 'percent') !== 'percent' && <div className='mb-3'>
                <label className='form-label'>Margin Fixed</label>
                <input className='form-control' type='number' value={marginSetupForm.marginFixed ?? 0} onChange={(e) => setMarginSetupForm((prev) => ({ ...prev, marginFixed: e.target.value }))} />
              </div>}
            </div>
            <div className='modal-footer'><button className='btn btn-secondary' onClick={() => setShowMarginSetupModal(false)}>Close</button><button className='btn btn-primary' onClick={saveMarginSetup}>Save</button></div>
          </div>
        </div>
      </div>
    )}
    {showMinimumMonthlyChargeModal && (
      <div className='modal show d-block'>
        <div className='modal-dialog modal-md'>
          <div className='modal-content'>
            <div className='modal-header'><h5 className='modal-title'>Minimum Monthly Charge</h5><button className='btn-close' onClick={() => setShowMinimumMonthlyChargeModal(false)} /></div>
            <div className='modal-body'>
              <div className='row g-3'>
                {minimumMonthlyChargeFields.filter((field) => !field.condition || field.condition(minimumMonthlyChargeForm)).map(renderMinimumMonthlyChargeField)}
              </div>
            </div>
            <div className='modal-footer'><button className='btn btn-secondary' onClick={() => setShowMinimumMonthlyChargeModal(false)}>Close</button><button className='btn btn-primary' onClick={saveMinimumMonthlyCharge}>Save</button></div>
          </div>
        </div>
      </div>
    )}

    {showInsuranceModal && (
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header"><h5 className="modal-title">Insurance Method</h5><button className="btn-close" onClick={() => setShowInsuranceModal(false)} /></div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Insurance Method</label>
                <select className="form-select" value={plantRateForm.insuranceCalculationMethod || 'percent_purchase_price'} onChange={(e) => setPlantRateForm((prev) => ({ ...prev, insuranceCalculationMethod: e.target.value }))}>
                  {insuranceCalculationMethodOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {(plantRateForm.insuranceCalculationMethod || 'percent_purchase_price') !== 'fixed' && <div className="mb-3">
                <label className="form-label">Insurance Rate (%)</label>
                <input className="form-control" type="number" value={plantRateForm.insuranceRate ?? 0} onChange={(e) => setPlantRateForm((prev) => ({ ...prev, insuranceRate: e.target.value }))} />
              </div>}
              <div className="mb-3">
                <label className="form-label">Insurance Monthly Amount</label>
                <input className="form-control" type="number" min="0" disabled={(plantRateForm.insuranceCalculationMethod || 'percent_purchase_price') !== 'fixed'} value={plantRateForm.insuranceMonthlyAmount ?? 0} onChange={(e) => setPlantRateForm((prev) => ({ ...prev, insuranceMonthlyAmount: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowInsuranceModal(false)}>Close</button><button className='btn btn-primary' onClick={saveInsuranceMethod}>Save</button></div>
          </div>
        </div>
      </div>
    )}
  </>;
};

export default PlantRatesLayer;
